import React, { useCallback, useEffect, useState, useRef, Children, Fragment, Component } from 'react';
import { TypeIcon, MailIcon, PhoneIcon, HashIcon, AlignLeftIcon, ChevronDownIcon, CircleIcon, CheckSquareIcon, SquareCheckIcon, LayoutListIcon, ColumnsIcon, TableIcon, CalendarIcon, UploadIcon, LinkIcon, EyeOffIcon, HeadingIcon, FileTextIcon, MinusIcon, MoveVerticalIcon, ImageIcon, SendIcon, PlayIcon, ShieldCheckIcon, ChevronRightIcon, CopyIcon, Trash2Icon, GripVerticalIcon, XIcon, UndoIcon, RedoIcon, SettingsIcon, WandSparklesIcon, Loader2Icon, PlusIcon, PaletteIcon, TagIcon, CodeIcon, PencilIcon, ZapIcon, SwatchBookIcon, SlidersHorizontalIcon, SparklesIcon, ImagePlusIcon, SearchIcon, EyeIcon } from 'lucide-react';
import { mediaLibraryApi, MediaLibraryRecord } from '../../services/creatorApi';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { FormBlock, FormTheme, FormBlockType, FormLogicRule, DEFAULT_FORM_THEME, FORM_PALETTE, FORM_STYLE_PRESETS, StylePreset, PaletteItem, createFormBlock, labelToName } from './types';
import { generateFormHtml } from './formHtmlGenerator';
import { callGPTAPI } from '../../utils/api';
import { LogicRulesEditor } from './LogicRulesEditor';
import { COUNTRY_CODES } from './countryPhoneCodes';
import { toast } from 'sonner';
const ICONS: Record<string, React.ElementType> = {
  Type: TypeIcon,
  Mail: MailIcon,
  Phone: PhoneIcon,
  Hash: HashIcon,
  AlignLeft: AlignLeftIcon,
  ChevronDown: ChevronDownIcon,
  Circle: CircleIcon,
  CheckSquare: CheckSquareIcon,
  SquareCheck: SquareCheckIcon,
  LayoutList: LayoutListIcon,
  Table: TableIcon,
  Columns: ColumnsIcon,
  Play: PlayIcon
};
const BEAUTIFY_INSTRUCTIONS = `You are an expert CSS designer. You receive a signup form's HTML and current CSS. The user will give you a specific instruction about what to change.

RULES:
1. Return ONLY a CSS code block — do NOT change the HTML at all.
2. Keep all existing class names (.fb-form, .fb-field, .fb-label, .fb-input, .fb-button, etc.)
3. CRITICAL: You must ONLY change the specific things the user asked for. Leave ALL other styles exactly as they are in the current CSS.
4. If the user says "make the button red", ONLY change the button color. Do NOT touch fonts, backgrounds, spacing, shadows, or anything else.
5. If the user says "change the font", ONLY change font-related properties. Do NOT touch colors, spacing, shadows, or anything else.
6. Start with the EXACT current CSS and make MINIMAL targeted changes based on the user's instruction.
7. Do NOT add any explanation — ONLY the CSS code block:
\`\`\`css
(your modified CSS here)
\`\`\`
8. The CSS should be self-contained and work with the provided HTML structure.
9. If no specific instruction is given, make subtle improvements while keeping the overall look intact — do NOT redesign from scratch.`;
function parseCssFromResponse(response: string): string {
  const cssMatch = response.match(/```css\s*\n([\s\S]*?)```/i);
  if (cssMatch) return cssMatch[1].trim();
  const genericMatch = response.match(/```\s*\n([\s\S]*?)```/i);
  if (genericMatch) return genericMatch[1].trim();
  return response.trim();
}
// ── Extracted renderers — placed before component to avoid bundler mangling ──
function getVideoEmbedUrl(url: string, autoplay?: boolean, muted?: boolean, loop?: boolean): string {
  var ytMatch = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([^&?]+)/);
  if (ytMatch && ytMatch[1]) {
    return 'https://www.youtube.com/embed/' + ytMatch[1] + '?rel=0' + (autoplay ? '&autoplay=1' : '') + (muted ? '&mute=1' : '') + (loop ? '&loop=1&playlist=' + ytMatch[1] : '');
  }
  var vmMatch = url.match(/vimeo\.com\/(?:video\/)?([0-9]+)/);
  if (vmMatch && vmMatch[1]) {
    return 'https://player.vimeo.com/video/' + vmMatch[1] + '?title=0&byline=0&portrait=0' + (autoplay ? '&autoplay=1' : '') + (muted ? '&muted=1' : '') + (loop ? '&loop=1' : '');
  }
  return url;
}
function getVideoRatioClass(ratio?: string): string {
  if (ratio === '4:3') return 'aspect-[4/3]';
  if (ratio === '1:1') return 'aspect-square';
  if (ratio === '9:16') return 'aspect-[9/16]';
  return 'aspect-video';
}
function renderVideoPreview(block: FormBlock) {
  if (!block.videoUrl) {
    return <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-400" data-id="element-643" data-mp="5g8lqi1vssp6">
        Video / GIF — set URL in properties
      </div>;
  }
  if (block.videoUrl.toLowerCase().endsWith('.gif')) {
    return <img src={block.videoUrl} alt="GIF" className="w-full h-auto rounded-md" data-id="element-644" data-mp="k7imihb5b5u6" />;
  }
  if (block.videoUrl.toLowerCase().endsWith('.mp4')) {
    return <video src={block.videoUrl} controls autoPlay={block.videoAutoplay} muted={block.videoMuted} loop={block.videoLoop} className="w-full h-auto rounded-md" data-id="element-645" data-mp="t7dwwjwvmwt5" />;
  }
  return <div className={'w-full relative ' + getVideoRatioClass(block.videoAspectRatio) + ' bg-gray-100 rounded-md overflow-hidden'} data-id="element-646" data-mp="28pbngwt60quh">
      <iframe src={getVideoEmbedUrl(block.videoUrl, block.videoAutoplay, block.videoMuted, block.videoLoop)} className="absolute top-0 left-0 w-full h-full" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen data-id="element-647" data-mp="1zqyi6unqaj5f" />
    </div>;
}
function renderImagePreview(block: FormBlock) {
  if (!block.src) {
    return <div className="bg-gray-100 border-2 border-dashed border-gray-300 rounded p-6 text-center text-sm text-gray-400" data-id="element-648" data-mp="3g4yja8yb8eu9">
        Image — set URL in properties
      </div>;
  }
  var sizing = block.imageSizing || 'maxWidth';
  var imgStyle: React.CSSProperties = {
    display: 'block'
  };
  if (sizing === 'maxWidth') {
    imgStyle.maxWidth = (block.imageMaxWidth || 100) + '%';
    imgStyle.height = 'auto';
  } else if (sizing === 'custom') {
    if (block.imageWidth) imgStyle.width = block.imageWidth + 'px';
    if (block.imageHeight) imgStyle.height = block.imageHeight + 'px';
    if (block.imageMaxWidth) imgStyle.maxWidth = block.imageMaxWidth + '%';
  }
  if (block.imageMaxHeight) imgStyle.maxHeight = block.imageMaxHeight + 'px';
  if (block.imageObjectFit) imgStyle.objectFit = block.imageObjectFit as any;
  if (block.imageBorderRadius) imgStyle.borderRadius = block.imageBorderRadius + 'px';
  if (block.imageOpacity != null && block.imageOpacity < 100) imgStyle.opacity = block.imageOpacity / 100;
  if (block.imageShadow === 'sm') imgStyle.boxShadow = '0 1px 2px 0 rgba(0,0,0,0.05)';else if (block.imageShadow === 'md') imgStyle.boxShadow = '0 4px 6px -1px rgba(0,0,0,0.1)';else if (block.imageShadow === 'lg') imgStyle.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.1)';
  var imgAlign = block.imageAlign || 'center';
  if (imgAlign === 'center') imgStyle.margin = '0 auto';else if (imgAlign === 'right') imgStyle.marginLeft = 'auto';
  var wrapStyle: React.CSSProperties = {
    textAlign: imgAlign
  };
  if (block.imagePosition && block.imagePosition !== 'static') {
    wrapStyle.position = block.imagePosition as any;
    if (block.imageTopOffset != null) wrapStyle.top = block.imageTopOffset + 'px';
    if (block.imageLeftOffset != null) wrapStyle.left = block.imageLeftOffset + 'px';
    if (block.imagePosition === 'sticky') wrapStyle.zIndex = 10;
    if (block.imagePosition === 'fixed') wrapStyle.zIndex = 1000;
    if (block.imagePosition === 'absolute') wrapStyle.zIndex = 10;
  }
  return <div style={wrapStyle} data-id="element-649" data-mp="1nm86pwld883f">
      <img src={block.src} alt={block.alt || ''} style={imgStyle} data-id="element-650" data-mp="1nnja3vawfdu0" />
    </div>;
}
function renderHeadingPreview(block: FormBlock, theme: FormTheme) {
  var level = block.level || 2;
  var headingStyle: React.CSSProperties = {
    fontSize: level === 1 ? 24 : level === 3 ? 16 : 20,
    color: theme.headingColor,
    textAlign: block.textAlign || 'left'
  };
  var text = block.content || 'Heading';
  if (level === 1) return <h1 className="font-semibold" style={headingStyle} data-id="element-651" data-mp="1bilsps9cehhy">
        {text}
      </h1>;
  if (level === 3) return <h3 className="font-semibold" style={headingStyle} data-id="element-652" data-mp="etegxbyizmr8">
        {text}
      </h3>;
  if (level === 4) return <h4 className="font-semibold" style={headingStyle} data-id="element-653" data-mp="itydoef6socu">
        {text}
      </h4>;
  return <h2 className="font-semibold" style={headingStyle} data-id="element-654" data-mp="nrgefhj1vx9r">
      {text}
    </h2>;
}
// ── Section canvas header — extracted from IIFE to avoid bundler mangling ──
function renderSectionCanvasHeader(block: FormBlock, theme: FormTheme): React.ReactElement {
  var showTitle = block.sectionShowTitle !== false;
  var anim = block.sectionAnimation && block.sectionAnimation !== 'none' ? block.sectionAnimation : null;
  var borderClass = block.sectionBorderStyle === 'dashed' ? 'border-dashed' : block.sectionBorderStyle === 'accent' ? 'border-l-4 border-l-blue-500' : block.sectionBorderStyle === 'none' ? 'border-transparent' : '';
  var children = block.children || [];
  return <div className={'rounded-lg border border-gray-200 overflow-hidden ' + borderClass} style={{
    background: block.sectionBackground || undefined
  }} data-id="element-655" data-mp="35z3su3p4mpln">
      {showTitle ? <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-656" data-mp="2w1631brmckqx">
          <LayoutListIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-657" data-mp="2r2cxc5ikpy1b" />
          <span className="text-sm font-semibold flex-1" style={{
        color: theme.headingColor
      }} data-id="element-658" data-mp="u9jjfva23s4h">
            {block.sectionTitle || 'Section'}
          </span>
          {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-659" data-mp="2cinhepu2plkj">
              {anim}
            </span>}
        </div> : <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-660" data-mp="2mhcy4fa8gzz7">
          <LayoutListIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-661" data-mp="2hk4hij4qh402" />
          <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-662" data-mp="1tv227hxknn8q">
            {block.sectionTitle || 'Section'} (title hidden)
          </span>
          {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-663" data-mp="23jjypmytj86k">
              {anim}
            </span>}
        </div>}
      <div className="p-3" data-id="element-664" data-mp="1yr8wgdglvaau">
        {children.length > 0 ? <div className="space-y-2" data-id="element-665" data-mp="1b1y49kdbmo8b">
            {children.map(function (child) {
          return <div key={child.id} className="text-xs text-gray-600 bg-white rounded-md px-3 py-2 border border-gray-200 flex items-center gap-2 group/child" data-id="element-666" data-mp="16463e0up7rrz">
                  <GripVerticalIcon className="h-3 w-3 text-gray-300 flex-shrink-0" data-id="element-667" data-mp="3mkkqx88ewt92" />
                  <span className="flex-1" data-id="element-668" data-mp="37vewryoflt81">{child.label || child.type}</span>
                  <span className="text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded" data-id="element-669" data-mp="3ctgheo1ia0lm">
                    {child.type}
                  </span>
                </div>;
        })}
          </div> : null}
        <div className="border-2 border-dashed border-gray-200 rounded-md p-3 text-center mt-2 hover:border-blue-300 hover:bg-blue-50/30 transition-colors" data-id="element-670" data-mp="99vhzs4xmsed">
          <p className="text-[10px] text-gray-400" data-id="element-671" data-mp="3vi4z714ozpt6">Drop fields here</p>
        </div>
      </div>
    </div>;
}
// ── Block Preview — placed before FormBuilder to avoid bundler mangling ──
function renderBlockPreview(block: FormBlock, theme: FormTheme): React.ReactElement {
  if (!block) return <div data-id="element-672" data-mp="qk8y5xsyn5n6" />;
  switch (block.type) {
    case 'heading':
      return renderHeadingPreview(block, theme);
    case 'paragraph':
      return <p className="text-sm" style={{
        color: theme.paragraphColor,
        textAlign: block.textAlign || 'left'
      }} data-id="element-673" data-mp="lojtxh5zx0kj">
          {block.content || 'Paragraph text'}
        </p>;
    case 'divider':
      return <hr className="border-gray-200" data-id="element-674" data-mp="7vketga4l8di" />;
    case 'spacer':
      return <div className="flex items-center justify-center text-xs text-gray-400" style={{
        height: block.height || 24
      }} data-id="element-675" data-mp="2vg8gj2xlu10">
          ↕ {block.height || 24}px
        </div>;
    case 'video_embed':
      return renderVideoPreview(block);
    case 'recaptcha':
      return <div className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-md max-w-[300px]" data-id="element-676" data-mp="1sa49jo62npjq">
          <div className="flex items-center gap-3" data-id="element-677" data-mp="1n9gn7fssfwvu">
            <div className="w-6 h-6 rounded-sm border-2 border-gray-300 bg-white" data-id="element-678" data-mp="1vrijyjk4dmy6"></div>
            <span className="text-sm font-medium text-gray-700" data-id="element-679" data-mp="1ztdjbiihpq9b">
              I'm not a robot
            </span>
          </div>
          <div className="flex flex-col items-center" data-id="element-680" data-mp="24lf3rrptanaf">
            <ShieldCheckIcon className="h-6 w-6 text-blue-500 mb-1" data-id="element-681" data-mp="29jow7uk5j9sc" />
            <span className="text-[10px] text-gray-500" data-id="element-682" data-mp="29m2v6ehyd656">reCAPTCHA</span>
          </div>
        </div>;
    case 'image':
      return renderImagePreview(block);
    case 'submit':
      return <div style={{
        textAlign: block.buttonAlign === 'center' ? 'center' : block.buttonAlign === 'right' ? 'right' : 'left'
      }} data-id="element-683" data-mp="1gxdqk5houioo">
          <button className={`px-5 py-2.5 text-sm font-semibold rounded-md ${block.buttonAlign === 'full' ? 'w-full' : ''}`} style={{
          backgroundColor: theme.buttonStyle === 'outline' ? 'transparent' : block.buttonColor || theme.primaryColor,
          color: theme.buttonStyle === 'outline' ? block.buttonColor || theme.primaryColor : theme.buttonTextColor,
          border: theme.buttonStyle === 'outline' ? `2px solid ${block.buttonColor || theme.primaryColor}` : 'none',
          borderRadius: theme.borderRadius,
          boxShadow: theme.buttonShadow ? '0 4px 14px 0 rgba(0,0,0,0.15)' : 'none'
        }} data-id="element-684" data-mp="2x8honh1kezi7">
            {block.buttonText || 'Submit'}
          </button>
        </div>;
    case 'consent':
      return <div data-id="element-685" data-mp="1goqwzn7qc9fu">
          <label className="flex items-start gap-2 cursor-pointer" data-id="element-686" data-mp="1gqbot52ntea0">
            <input type="checkbox" disabled className="mt-0.5" style={{
            accentColor: theme.primaryColor
          }} data-id="element-687" data-mp="1bqz34nniyd90" />
            <span className="text-sm" style={{
            color: theme.labelColor,
            lineHeight: 1.4
          }} data-id="element-688" data-mp="20blox7g2sd5d">
              {block.consentText || 'I agree to receive updates'}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-689" data-mp="1qez1qc0a1a06">*</span>}
            </span>
          </label>
          {block.helpText && <p className="text-xs text-gray-400 mt-1 ml-6" data-id="element-690" data-mp="1hczjjgdizzvn">{block.helpText}</p>}
        </div>;
    case 'hidden':
      return <div className="text-xs text-gray-400 bg-gray-50 px-2 py-1 rounded font-mono" data-id="element-691" data-mp="12pej5u81cerf">
          hidden: {block.name} = {block.defaultValue || '(empty)'}
        </div>;
    case 'section':
      {
        var showTitle = block.sectionShowTitle !== false;
        var anim = block.sectionAnimation && block.sectionAnimation !== 'none' ? block.sectionAnimation : null;
        var borderClass = block.sectionBorderStyle === 'dashed' ? 'border-dashed' : block.sectionBorderStyle === 'accent' ? 'border-l-4 border-l-blue-500' : block.sectionBorderStyle === 'none' ? 'border-transparent' : '';
        return <div className={`rounded-lg border border-gray-200 overflow-hidden ${borderClass}`} style={{
          background: block.sectionBackground || undefined
        }} data-id="element-692" data-mp="2lo2msjhd8r7w">
          {showTitle && <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-693" data-mp="39bkc88d5rd7p">
              <LayoutListIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-694" data-mp="35aiig720ftd4" />
              <span className="text-sm font-semibold flex-1" style={{
              color: theme.headingColor
            }} data-id="element-695" data-mp="35clbz12dg4ml">
                {block.sectionTitle || 'Section'}
              </span>
              {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-696" data-mp="9evr5rdq2yjg">
                  {anim}
                </span>}
            </div>}
          {!showTitle && <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-697" data-mp="n461z42p7ekf">
              <LayoutListIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-698" data-mp="s4240xru4vxc" />
              <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-699" data-mp="x2lr51i60dra">
                {block.sectionTitle || 'Section'} (title hidden)
              </span>
              {anim && <span className="text-[9px] px-1.5 py-0.5 bg-purple-100 text-purple-600 rounded-full font-medium" data-id="element-700" data-mp="112e5xvy5jg53">
                  {anim}
                </span>}
            </div>}
          <div className="p-3" data-id="element-701" data-mp="37uonyj69vrvs">
            {block.children && block.children.length > 0 ? <div className="space-y-2" data-id="element-702" data-mp="37whttw92a82q">
                {block.children.map(child => <div key={child.id} className="text-xs text-gray-600 bg-white rounded-md px-3 py-2 border border-gray-200 flex items-center gap-2 group/child" data-id="element-703" data-mp="1dkggci4rdddf">
                    <GripVerticalIcon className="h-3 w-3 text-gray-300 flex-shrink-0" data-id="element-704" data-mp="1idt6wxszzu8t" />
                    <span className="flex-1" data-id="element-705" data-mp="unyu2g8oi5ok">{child.label || child.type}</span>
                    <span className="text-[9px] text-gray-400 bg-gray-50 px-1.5 py-0.5 rounded" data-id="element-706" data-mp="uq1hqtmczmq4">
                      {child.type}
                    </span>
                  </div>)}
              </div> : null}
            <div className="border-2 border-dashed border-gray-200 rounded-md p-3 text-center mt-2 hover:border-blue-300 hover:bg-blue-50/30 transition-colors" data-id="element-707" data-mp="zmif8efg7dfv">
              <p className="text-[10px] text-gray-400" data-id="element-708" data-mp="2g3e8t1j3z5lr">Drop fields here</p>
            </div>
          </div>
        </div>;
      }
    case 'subform':
      {
        var columns = block.subformColumns || [{
          label: 'Column 1',
          type: 'text'
        }, {
          label: 'Column 2',
          type: 'text'
        }];
        var showName = block.subformShowName !== false;
        var minR = block.subformMinRows || 1;
        var maxR = block.subformMaxRows || 10;
        return <div className="rounded-lg border border-gray-200 overflow-hidden bg-white" data-id="element-709" data-mp="giexyz98oqj0">
          {showName ? <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200" data-id="element-710" data-mp="kjs4lbk5oxqz">
              <TableIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-711" data-mp="6svcdlu56ikg" />
              <span className="text-sm font-semibold flex-1" style={{
              color: theme.headingColor
            }} data-id="element-712" data-mp="6qsq9cw419l0">
                {block.subformName || 'Subform'}
              </span>
              <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-713" data-mp="1492iql497amz">
                {minR}–{maxR} rows
              </span>
            </div> : <div className="flex items-center gap-2 px-3 py-1.5 bg-gray-50/50 border-b border-gray-100" data-id="element-714" data-mp="1ph4p74a8rnuf">
              <TableIcon className="h-3.5 w-3.5 text-gray-300 flex-shrink-0" data-id="element-715" data-mp="1bpzsa9y9d9ns" />
              <span className="text-[10px] text-gray-400 italic flex-1" data-id="element-716" data-mp="1fqjsjme4ote5">
                {block.subformName || 'Subform'} (name hidden)
              </span>
              <span className="text-[9px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-717" data-mp="11ppean4efy7k">
                {minR}–{maxR} rows
              </span>
            </div>}
          <div className="overflow-x-auto" data-id="element-718" data-mp="srnjwqf683t9">
            <table className="w-full text-left border-collapse" data-id="element-719" data-mp="ssowizbu4yd3">
              <thead data-id="element-720" data-mp="wsfsrik4s6p0">
                <tr className="border-b border-gray-200 bg-gray-50/50" data-id="element-721" data-mp="wu0eciogwfaq">
                  <th className="px-3 py-2 text-xs font-medium text-gray-500 w-8" data-id="element-722" data-mp="2n4v7bx0thd4v">
                    #
                  </th>
                  {columns.map((col, i) => <th key={i} className="px-3 py-2 text-xs font-medium text-gray-500" data-id="element-723" data-mp="fhtun6l4hxy5" data-mpi={i}>
                      {col.label}
                      {col.required && <span className="text-red-500 ml-0.5" data-id="element-724" data-mp="p9pqsc3nfdzq">*</span>}
                    </th>)}
                  <th className="px-3 py-2 w-8" data-id="element-725" data-mp="z6kr90jhfogn"></th>
                </tr>
              </thead>
              <tbody data-id="element-726" data-mp="u7hv65lhcskt">
                <tr className="border-b border-gray-100" data-id="element-727" data-mp="u811sq5ojujj">
                  <td className="px-3 py-1.5 text-gray-400" data-id="element-728" data-mp="u9lv6cg9d7ft">1</td>
                  {columns.map((col, i) => <td key={i} className="px-3 py-1.5" data-id="element-729" data-mp="1386970avisxp" data-mpi={i}>
                      <div className="w-full px-2 py-1 rounded text-gray-400 text-xs" style={{
                      border: `1px solid ${theme.inputBorderColor}`,
                      background: theme.inputBackground,
                      borderRadius: theme.borderRadius
                    }} data-id="element-730" data-mp="138pffjr5vsvw">
                        {col.placeholder || col.type}
                      </div>
                    </td>)}
                  <td className="px-3 py-1.5" data-id="element-731" data-mp="1italtotqpubm">
                    <Trash2Icon className="h-3 w-3 text-gray-300" data-id="element-732" data-mp="1mwp30itf0gqn" />
                  </td>
                </tr>
                <tr className="border-b border-gray-100" data-id="element-733" data-mp="1rw1xnpqj2s3s">
                  <td className="px-3 py-1.5 text-gray-400" data-id="element-734" data-mp="1rtfqvsbgogmm">2</td>
                  {columns.map((_, i) => <td key={i} className="px-3 py-1.5" data-id="element-735" data-mp="21l3c3bhfd1fn" data-mpi={i}>
                      <div className="w-full px-2 py-1 rounded text-gray-300" style={{
                      border: `1px dashed ${theme.inputBorderColor}`,
                      background: theme.inputBackground,
                      borderRadius: theme.borderRadius
                    }} data-id="element-736" data-mp="21llih0xsww1a">
                        &nbsp;
                      </div>
                    </td>)}
                  <td className="px-3 py-1.5" data-id="element-737" data-mp="3v30yzwj4g9dk">
                    <Trash2Icon className="h-3 w-3 text-gray-300" data-id="element-738" data-mp="3r27bh49lcwl7" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between px-3 py-2 border-t border-gray-100" data-id="element-739" data-mp="3m4p0l8c0k7ak">
            <span className="inline-flex items-center gap-1 text-[11px] font-medium text-gray-400 cursor-default" data-id="element-740" data-mp="mnhb7d17zj5w">
              <PlusIcon className="h-3 w-3" data-id="element-741" data-mp="ioi7twv4zugk" /> Add Row
            </span>
            <span className="text-[9px] text-gray-400" data-id="element-742" data-mp="dudzb9683edd">
              {columns.length} field{columns.length !== 1 ? 's' : ''}
            </span>
          </div>
        </div>;
      }
    case 'phone':
      {
        var defaultCountry = block.phoneDefaultCountry || 'US';
        var country = COUNTRY_CODES.find(function (c) {
          return c.iso === defaultCountry;
        }) || COUNTRY_CODES.find(function (c) {
          return c.iso === 'US';
        });
        return <div data-id="element-743" data-mp="q5wlg8u4d0gf">
          <div className="flex items-center gap-1 mb-1" data-id="element-744" data-mp="q5mxsrwjr5dw">
            <label className="block" style={{
              fontSize: theme.labelFontSize,
              fontWeight: Number(theme.labelFontWeight),
              color: theme.labelColor
            }} data-id="element-745" data-mp="c5c31ds496ng">
              {block.label}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-746" data-mp="18v35q0aka04c">*</span>}
            </label>
            {block.width === 'half' && <span className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded font-medium" data-id="element-747" data-mp="v2vfkrds97em">
                ½
              </span>}
          </div>
          <div className="w-full flex items-stretch text-sm text-gray-400 overflow-hidden" style={{
            borderRadius: theme.borderRadius,
            border: `1px solid ${theme.inputBorderColor}`,
            background: theme.inputBackground,
            boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
          }} data-id="element-748" data-mp="1nsvnkz3kz0lp">
            <div className="flex items-center gap-1.5 px-3 bg-gray-50/50 border-r" style={{
              borderColor: theme.inputBorderColor
            }} data-id="element-749" data-mp="2bmvy0b0hm8nq">
              <span data-id="element-750" data-mp="1j0b6nak0hwk6">{country ? country.flag : ''}</span>
              <span className="text-gray-600 font-medium" data-id="element-751" data-mp="1exocv43s61kc">
                {country ? country.dialCode : ''}
              </span>
              <ChevronDownIcon className="h-3 w-3 text-gray-400 ml-1" data-id="element-752" data-mp="1sxpn3a5xqlch" />
            </div>
            <div className="flex-1 px-3 py-2 flex items-center" data-id="element-753" data-mp="1nxtib8peypvv">
              {block.placeholder || '(555) 000-0000'}
            </div>
          </div>
          {block.helpText && <p className="text-xs text-gray-400 mt-1" data-id="element-754" data-mp="2zdrxzvf3ka62">{block.helpText}</p>}
        </div>;
      }
    default:
      return <div data-id="element-755" data-mp="37dfrwt9tw8zn">
          <div className="flex items-center gap-1 mb-1" data-id="element-756" data-mp="37do9gygwc2h6">
            <label className="block" style={{
            fontSize: theme.labelFontSize,
            fontWeight: Number(theme.labelFontWeight),
            color: theme.labelColor
          }} data-id="element-757" data-mp="3bf9oapczqqu0">
              {block.label}
              {block.required && <span className="text-red-500 ml-0.5" data-id="element-758" data-mp="2nqqfm8q14c1e">*</span>}
            </label>
            {block.width === 'half' && <span className="text-[10px] px-1 py-0.5 bg-blue-50 text-blue-600 rounded font-medium" data-id="element-759" data-mp="2sq33mwsmzp5k">
                ½
              </span>}
          </div>
          {block.type === 'textarea' ? <div className="w-full h-20 px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-760" data-mp="32gpbu5y0xde8">
              {block.placeholder}
            </div> : block.type === 'select' ? <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground
        }} data-id="element-761" data-mp="1sj7opprcebr0">
              {block.placeholder || 'Select...'}
            </div> : block.type === 'radio' || block.type === 'checkbox' ? <div className={`mt-1 ${block.optionsLayout === 'horizontal' ? 'flex flex-wrap gap-x-4 gap-y-1' : block.optionsColumns && block.optionsColumns > 1 ? 'grid gap-x-4 gap-y-1' : 'space-y-1'}`} style={block.optionsColumns && block.optionsColumns > 1 && block.optionsLayout !== 'horizontal' ? {
          gridTemplateColumns: `repeat(${block.optionsColumns}, minmax(0, 1fr))`
        } : undefined} data-id="element-762" data-mp="14s2b7h8vjvjv">
              {(block.validation.options || []).map(function (opt, i) {
            return <label key={i} className="flex items-center gap-2 text-sm text-gray-600" data-id="element-763" data-mp="20x2f5k5q2114" data-mpi={i}>
                    <input type={block.type === 'radio' ? 'radio' : 'checkbox'} disabled className="accent-blue-600" style={{
                accentColor: theme.primaryColor
              }} data-id="element-764" data-mp="25okg487qw1m3" />
                    {opt.label}
                  </label>;
          })}
            </div> : <div className="w-full px-3 py-2 text-sm text-gray-400" style={{
          borderRadius: theme.borderRadius,
          border: `1px solid ${theme.inputBorderColor}`,
          background: theme.inputBackground,
          boxShadow: theme.inputShadow ? '0 1px 3px 0 rgba(0,0,0,0.08)' : 'none'
        }} data-id="element-765" data-mp="1m1mgf1u75gye">
              {block.placeholder || block.type}
            </div>}
          {block.helpText && <p className="text-xs text-gray-400 mt-1" data-id="element-766" data-mp="1hc285e8ndpfo">{block.helpText}</p>}
        </div>;
  }
}
// ── Shared UI helpers — defined before components to avoid bundler mangling ──
var PropField = function (props: any) {
  var label = props.label;
  var children = props.children;
  return <div className="space-y-1.5" data-id="element-767" data-mp="2501xqau839so">
      <label className="block text-xs font-medium text-gray-700" data-id="element-768" data-mp="251cys2waa3g3">{label}</label>
      {children}
    </div>;
};
var ColorInput = function (props: any) {
  var value = props.value;
  var onChange = props.onChange;
  return <div className="flex items-center gap-2" data-id="element-769" data-mp="1sgqug35zj59u">
      <input type="color" value={value || '#000000'} onChange={function (e: any) {
      onChange(e.target.value);
    }} className="h-8 w-8 rounded border border-gray-300 cursor-pointer p-0" data-id="element-770" data-mp="1oh005ph9270k" />
      <input type="text" value={value || ''} onChange={function (e: any) {
      onChange(e.target.value);
    }} className="prop-input flex-1" placeholder="#000000" data-id="element-771" data-mp="1eiu0zfmtveze" />
    </div>;
};
interface FormBuilderProps {
  initialBlocks?: FormBlock[];
  initialTheme?: FormTheme;
  initialRules?: FormLogicRule[];
  onSchemaChange: (blocks: FormBlock[], theme: FormTheme, rules: FormLogicRule[]) => void;
  onHtmlChange: (html: string, css: string) => void;
  settingsSidebar?: React.ReactNode;
  hideLogicRules?: boolean;
  customPalette?: PaletteItem[];
}
/** Group consecutive half-width blocks into rows for canvas display */
interface CanvasRow {
  type: 'full' | 'half-row';
  blocks: {
    block: FormBlock;
    flatIndex: number;
  }[];
}
function groupBlocksIntoRows(blocks: FormBlock[]): CanvasRow[] {
  const rows: CanvasRow[] = [];
  let i = 0;
  while (i < blocks.length) {
    const block = blocks[i];
    if (block.width === 'half') {
      const rowBlocks: {
        block: FormBlock;
        flatIndex: number;
      }[] = [{
        block: block,
        flatIndex: i
      }];
      // Check if next block is also half
      if (i + 1 < blocks.length && blocks[i + 1].width === 'half') {
        rowBlocks.push({
          block: blocks[i + 1],
          flatIndex: i + 1
        });
        i += 2;
      } else {
        i += 1;
      }
      rows.push({
        type: 'half-row',
        blocks: rowBlocks
      });
    } else {
      rows.push({
        type: 'full',
        blocks: [{
          block: block,
          flatIndex: i
        }]
      });
      i += 1;
    }
  }
  return rows;
}
export function FormBuilder({
  initialBlocks,
  initialTheme,
  initialRules,
  onSchemaChange,
  onHtmlChange,
  settingsSidebar,
  hideLogicRules,
  customPalette
}: FormBuilderProps) {
  const [blocks, setBlocks] = useState<FormBlock[]>(initialBlocks || []);
  const [theme, setTheme] = useState<FormTheme>(initialTheme || DEFAULT_FORM_THEME);
  const [rules, setRules] = useState<FormLogicRule[]>(initialRules || []);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeRightTab, setActiveRightTab] = useState<'properties' | 'logic'>('properties');
  const [showLogicEditor, setShowLogicEditor] = useState(false);
  const [dragOverIdx, setDragOverIdx] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [history, setHistory] = useState<FormBlock[][]>([initialBlocks || []]);
  const [historyIdx, setHistoryIdx] = useState(0);
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(new Set());
  const [isBeautifying, setIsBeautifying] = useState(false);
  const [beautifyPrompt, setBeautifyPrompt] = useState('');
  const [showBeautifyInput, setShowBeautifyInput] = useState(false);
  const [sectionSettingsBlock, setSectionSettingsBlock] = useState<FormBlock | null>(null);
  // Column configuration modal state
  const [columnConfigModal, setColumnConfigModal] = useState<{
    isOpen: boolean;
    blockId: string;
    currentCount: number;
  } | null>(null);
  // Track selected child block inside a column: { parentId, colIndex, childId }
  const [selectedChild, setSelectedChild] = useState<{
    parentId: string;
    colIndex: number;
    childId: string;
  } | null>(null);
  // Track drag over column: { parentId, colIndex, position }
  const [dragOverCol, setDragOverCol] = useState<{
    parentId: string;
    colIndex: number;
    position: number;
  } | null>(null);
  // Track CSS before beautify so undo can revert it
  const [preBeautifyCss, setPreBeautifyCss] = useState<string | null>(null);
  const dragItemRef = useRef<{
    type: 'palette' | 'canvas';
    blockType?: FormBlockType;
    index?: number;
  } | null>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const selectedBlock = (() => {
    if (!selectedId) return null;
    // Check top-level
    const top = blocks.find(b => b.id === selectedId);
    if (top) return top;
    // Search inside section children and columns
    for (const b of blocks) {
      if (b.type === 'section' && b.children) {
        const child = b.children.find(c => c.id === selectedId);
        if (child) return child;
      }
      if (b.type === 'columns' && b.columns) {
        for (const col of b.columns) {
          const child = col.find(c => c.id === selectedId);
          if (child) return child;
        }
      }
    }
    return null;
  })();
  const pushHistory = useCallback((newBlocks: FormBlock[]) => {
    setHistory(prev => {
      const trimmed = prev.slice(0, historyIdx + 1);
      return [...trimmed, newBlocks].slice(-50);
    });
    setHistoryIdx(prev => Math.min(prev + 1, 49));
  }, [historyIdx]);
  const updateBlocks = useCallback((newBlocks: FormBlock[]) => {
    setBlocks(newBlocks);
    pushHistory(newBlocks);
    const {
      html,
      css
    } = generateFormHtml(newBlocks, theme);
    onSchemaChange(newBlocks, theme, rules);
    onHtmlChange(html, css);
  }, [theme, rules, pushHistory, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    // Only sync generated HTML when the builder has blocks.
    // This prevents empty builder initialization from clearing the code editor.
    if (blocks.length > 0) {
      const {
        html,
        css
      } = generateFormHtml(blocks, theme);
      onSchemaChange(blocks, theme, rules);
      onHtmlChange(html, css);
    }
  }, [theme, rules]);
  const undo = useCallback(() => {
    // If we have a beautify CSS to revert, do that first
    if (preBeautifyCss !== null) {
      const {
        html
      } = generateFormHtml(blocks, theme);
      onHtmlChange(html, preBeautifyCss);
      setPreBeautifyCss(null);
      return;
    }
    if (historyIdx > 0) {
      const newIdx = historyIdx - 1;
      setHistoryIdx(newIdx);
      const prev = history[newIdx];
      setBlocks(prev);
      const {
        html,
        css
      } = generateFormHtml(prev, theme);
      onSchemaChange(prev, theme, rules);
      onHtmlChange(html, css);
    }
  }, [historyIdx, history, theme, rules, onSchemaChange, onHtmlChange, preBeautifyCss, blocks]);
  const redo = useCallback(() => {
    if (historyIdx < history.length - 1) {
      const newIdx = historyIdx + 1;
      setHistoryIdx(newIdx);
      const next = history[newIdx];
      setBlocks(next);
      const {
        html,
        css
      } = generateFormHtml(next, theme);
      onSchemaChange(next, theme, rules);
      onHtmlChange(html, css);
    }
  }, [historyIdx, history, theme, rules, onSchemaChange, onHtmlChange]);
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();else undo();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [undo, redo]);
  // AI Beautify handler
  const handleBeautify = async (styleDirection?: string) => {
    if (blocks.length === 0) return;
    setIsBeautifying(true);
    try {
      const {
        html,
        css
      } = generateFormHtml(blocks, theme);
      // Save current CSS so undo can revert
      setPreBeautifyCss(css);
      const prompt = `Here is my form HTML:\n\`\`\`html\n${html}\n\`\`\`\n\nHere is the current CSS:\n\`\`\`css\n${css}\n\`\`\`\n\n${styleDirection ? `User instruction: ${styleDirection}` : 'Make subtle improvements to polish the form. Keep the overall look intact.'}`;
      const result = await callGPTAPI(BEAUTIFY_INSTRUCTIONS, prompt);
      const newCss = parseCssFromResponse(result);
      if (newCss) {
        onHtmlChange(html, newCss);
      }
    } catch (err) {
      console.error('Beautify failed:', err);
      setPreBeautifyCss(null); // Clear on failure
    } finally {
      setIsBeautifying(false);
      setShowBeautifyInput(false);
      setBeautifyPrompt('');
    }
  };
  // Drag handlers
  const handlePaletteDragStart = (type: FormBlockType) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'palette',
      blockType: type
    };
    e.dataTransfer.effectAllowed = 'copy';
    setIsDragging(true);
  };
  const handleCanvasDragStart = (index: number) => (e: React.DragEvent) => {
    dragItemRef.current = {
      type: 'canvas',
      index
    };
    e.dataTransfer.effectAllowed = 'move';
    setIsDragging(true);
  };
  const handleDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(index);
    setDragOverCol(null);
  };
  const handleDragLeave = () => {
    setDragOverIdx(null);
  };
  const handleDragEnd = () => {
    setIsDragging(false);
    setDragOverIdx(null);
    dragItemRef.current = null;
  };
  const handleBlockDragOver = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    e.dataTransfer.dropEffect = dragItemRef.current?.type === 'palette' ? 'copy' : 'move';
    setDragOverIdx(dropIdx);
    setDragOverCol(null);
  };
  const handleBlockDrop = (index: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const midY = rect.top + rect.height / 2;
    const dropIdx = e.clientY < midY ? index : index + 1;
    setDragOverIdx(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIdx, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      // Open config modal if it's a columns block
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentCount: newBlock.columnCount || 2
        });
      }
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      if (drag.index === index) {
        dragItemRef.current = null;
        return;
      }
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIdx ? dropIdx - 1 : dropIdx;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleDrop = (dropIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = [...blocks];
      newBlocks.splice(dropIndex, 0, newBlock);
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      // Open config modal if it's a columns block
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentCount: newBlock.columnCount || 2
        });
      }
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const newBlocks = [...blocks];
      const [moved] = newBlocks.splice(drag.index, 1);
      const adjustedIdx = drag.index < dropIndex ? dropIndex - 1 : dropIndex;
      newBlocks.splice(adjustedIdx, 0, moved);
      updateBlocks(newBlocks);
    }
    dragItemRef.current = null;
  };
  const handleCanvasDropEnd = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOverIdx(null);
    setDragOverCol(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType) {
      const newBlock = createFormBlock(drag.blockType);
      updateBlocks([...blocks, newBlock]);
      setSelectedId(newBlock.id);
      setSelectedChild(null);
      // Open config modal if it's a columns block
      if (newBlock.type === 'columns') {
        setColumnConfigModal({
          isOpen: true,
          blockId: newBlock.id,
          currentCount: newBlock.columnCount || 2
        });
      }
    }
    dragItemRef.current = null;
  };
  // Column drop handlers
  const handleColumnDragOver = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const drag = dragItemRef.current;
    if (!drag) return;
    // Don't allow sections, subforms, or columns inside columns
    if (drag.type === 'palette' && drag.blockType && !['section', 'subform', 'columns'].includes(drag.blockType)) {
      const parent = blocks.find(b => b.id === parentId);
      const col = parent?.columns?.[colIndex] || [];
      setDragOverCol({
        parentId,
        colIndex,
        position: col.length
      });
      setDragOverIdx(null);
      e.dataTransfer.dropEffect = 'copy';
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const sourceBlock = blocks[drag.index];
      if (sourceBlock && !['section', 'subform', 'columns'].includes(sourceBlock.type)) {
        const parent = blocks.find(b => b.id === parentId);
        const col = parent?.columns?.[colIndex] || [];
        setDragOverCol({
          parentId,
          colIndex,
          position: col.length
        });
        setDragOverIdx(null);
        e.dataTransfer.dropEffect = 'move';
      }
    }
  };
  const handleColumnDrop = (parentId: string, colIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverCol(null);
    setDragOverIdx(null);
    setIsDragging(false);
    const drag = dragItemRef.current;
    if (!drag) return;
    if (drag.type === 'palette' && drag.blockType && !['section', 'subform', 'columns'].includes(drag.blockType)) {
      const newBlock = createFormBlock(drag.blockType);
      const newBlocks = blocks.map(b => {
        if (b.id !== parentId) return b;
        const cols = (b.columns || []).map((col, i) => i === colIndex ? [...col, newBlock] : col);
        return {
          ...b,
          columns: cols
        };
      });
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
      setSelectedChild({
        parentId,
        colIndex,
        childId: newBlock.id
      });
    } else if (drag.type === 'palette' && drag.blockType && ['section', 'subform', 'columns'].includes(drag.blockType)) {
      toast.error('Cannot nest this block inside a column', {
        description: `${drag.blockType} blocks cannot be placed inside columns.`
      });
    } else if (drag.type === 'canvas' && drag.index !== undefined) {
      const sourceBlock = blocks[drag.index];
      if (!sourceBlock || ['section', 'subform', 'columns'].includes(sourceBlock.type)) {
        if (sourceBlock) toast.error('Cannot nest this block inside a column', {
          description: `${sourceBlock.type} blocks cannot be placed inside columns.`
        });
        return;
      }
      // Remove from top-level
      const remaining = blocks.filter((_, i) => i !== drag.index);
      // Add to column
      const newBlocks = remaining.map(b => {
        if (b.id !== parentId) return b;
        const cols = (b.columns || []).map((col, i) => i === colIndex ? [...col, sourceBlock] : col);
        return {
          ...b,
          columns: cols
        };
      });
      updateBlocks(newBlocks);
      setSelectedId(sourceBlock.id);
      setSelectedChild({
        parentId,
        colIndex,
        childId: sourceBlock.id
      });
    }
    dragItemRef.current = null;
  };
  const deleteChildBlock = (parentId: string, colIndex: number, childId: string) => {
    const newBlocks = blocks.map(b => {
      if (b.id !== parentId) return b;
      const cols = (b.columns || []).map((col, i) => i === colIndex ? col.filter(cb => cb.id !== childId) : col);
      return {
        ...b,
        columns: cols
      };
    });
    updateBlocks(newBlocks);
    if (selectedId === childId) {
      setSelectedId(null);
      setSelectedChild(null);
    }
  };
  const deleteBlock = (id: string) => {
    // Check if it's a top-level block
    if (blocks.some(b => b.id === id)) {
      updateBlocks(blocks.filter(b => b.id !== id));
    } else {
      // Check inside section children and columns
      const newBlocks = blocks.map(b => {
        if (b.type === 'section' && b.children) {
          const filtered = b.children.filter(c => c.id !== id);
          if (filtered.length !== b.children.length) {
            return {
              ...b,
              children: filtered
            };
          }
        }
        if (b.type === 'columns' && b.columns) {
          const newCols = b.columns.map(col => {
            const filtered = col.filter(c => c.id !== id);
            return filtered;
          });
          return {
            ...b,
            columns: newCols
          };
        }
        return b;
      });
      updateBlocks(newBlocks);
    }
    if (selectedId === id) {
      setSelectedId(null);
      setSelectedChild(null);
    }
  };
  const duplicateBlock = (id: string) => {
    const idx = blocks.findIndex(b => b.id === id);
    if (idx === -1) return;
    const clone = {
      ...blocks[idx],
      id: Math.random().toString(36).substr(2, 9),
      name: blocks[idx].name + '_copy'
    };
    const newBlocks = [...blocks];
    newBlocks.splice(idx + 1, 0, clone);
    updateBlocks(newBlocks);
    setSelectedId(clone.id);
  };
  const updateBlock = (id: string, updates: Partial<FormBlock>) => {
    const newBlocks = blocks.map(b => {
      if (b.id === id) {
        return {
          ...b,
          ...updates
        };
      }
      // Also check section children
      if (b.type === 'section' && b.children) {
        const childIdx = b.children.findIndex(c => c.id === id);
        if (childIdx !== -1) {
          const newChildren = [...b.children];
          newChildren[childIdx] = {
            ...newChildren[childIdx],
            ...updates
          };
          return {
            ...b,
            children: newChildren
          };
        }
      }
      // Also check columns
      if (b.type === 'columns' && b.columns) {
        const newCols = b.columns.map(col => {
          const childIdx = col.findIndex(c => c.id === id);
          if (childIdx !== -1) {
            const newCol = [...col];
            newCol[childIdx] = {
              ...newCol[childIdx],
              ...updates
            };
            return newCol;
          }
          return col;
        });
        return {
          ...b,
          columns: newCols
        };
      }
      return b;
    });
    setBlocks(newBlocks);
    const {
      html,
      css
    } = generateFormHtml(newBlocks, theme);
    onSchemaChange(newBlocks, theme, rules);
    onHtmlChange(html, css);
  };
  const commitToHistory = () => pushHistory(blocks);
  const activePalette = customPalette || FORM_PALETTE;
  const categories = [...new Set(activePalette.map(p => p.category))];
  const toggleCat = (cat: string) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat);else next.add(cat);
      return next;
    });
  };
  // Group blocks into rows for canvas display
  const canvasRows = groupBlocksIntoRows(blocks);
  // Handle drop into a section block
  const handleSectionDrop = (sectionId: string) => (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const ref = dragItemRef.current;
    if (!ref) return;
    if (ref.type === 'palette' && ref.blockType) {
      // Don't allow nesting sections, columns, or subforms inside sections
      if (ref.blockType === 'section' || ref.blockType === 'columns' || ref.blockType === 'subform' || ref.blockType === 'submit') {
        toast.error('Cannot nest this block inside a section', {
          description: `${ref.blockType} blocks cannot be placed inside sections.`
        });
        return;
      }
      const newBlock = createFormBlock(ref.blockType);
      const newBlocks = blocks.map(b => {
        if (b.id === sectionId) {
          return {
            ...b,
            children: [...(b.children || []), newBlock]
          };
        }
        return b;
      });
      updateBlocks(newBlocks);
      setSelectedId(newBlock.id);
    } else if (ref.type === 'canvas' && ref.index !== undefined) {
      // Move an existing block into the section
      const sourceBlock = blocks[ref.index];
      if (!sourceBlock || sourceBlock.type === 'section' || sourceBlock.type === 'columns' || sourceBlock.type === 'subform' || sourceBlock.type === 'submit') return;
      if (sourceBlock.id === sectionId) return;
      // Remove from top-level
      const remaining = blocks.filter((_, i) => i !== ref.index);
      // Add to section children
      const newBlocks = remaining.map(b => {
        if (b.id === sectionId) {
          return {
            ...b,
            children: [...(b.children || []), sourceBlock]
          };
        }
        return b;
      });
      updateBlocks(newBlocks);
      setSelectedId(sourceBlock.id);
    }
    dragItemRef.current = null;
    setDragOverIdx(null);
  };
  // Remove a child from a section
  const removeChildFromSection = (sectionId: string, childId: string) => {
    const section = blocks.find(b => b.id === sectionId);
    if (!section) return;
    const child = (section.children || []).find(c => c.id === childId);
    if (!child) return;
    // Remove from section children
    const sectionIdx = blocks.findIndex(b => b.id === sectionId);
    const newBlocks = blocks.map(b => {
      if (b.id === sectionId) {
        return {
          ...b,
          children: (b.children || []).filter(c => c.id !== childId)
        };
      }
      return b;
    });
    // Insert the child after the section in the top-level list
    newBlocks.splice(sectionIdx + 1, 0, child);
    updateBlocks(newBlocks);
  };
  // Render a single block card (used for both full and half-width)
  var renderBlockCard = function (block: any, flatIndex: any) {
    return <div key={block.id} draggable onDragStart={handleCanvasDragStart(flatIndex)} onDragEnd={handleDragEnd} onDragOver={block.type === 'section' || block.type === 'columns' ? undefined : handleBlockDragOver(flatIndex)} onDrop={block.type === 'section' || block.type === 'columns' ? undefined : handleBlockDrop(flatIndex)} onClick={e => {
      e.stopPropagation();
      setSelectedId(block.id);
      setSelectedChild(null);
      setActiveRightTab('properties');
    }} className={`relative group border-2 rounded-lg transition-all ${selectedId === block.id && !selectedChild ? 'border-blue-500 shadow-sm bg-blue-50/10' : 'border-transparent hover:border-gray-300 bg-white'}`} data-id="element-772" data-mp="1vpb3rb9g5k3t">
        {/* Block Actions */}
        <div className={`absolute -right-3 -top-3 flex items-center gap-1 z-10 transition-opacity ${selectedId === block.id && !selectedChild ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} data-id="element-773" data-mp="2js7ogi4il1gs">
          {block.type === 'section' && <button onClick={e => {
          e.stopPropagation();
          setSectionSettingsBlock(block);
        }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 text-gray-600" title="Section Settings" data-id="element-774" data-mp="2tkn4vb05xhzk">
              <SettingsIcon className="h-3.5 w-3.5" data-id="element-775" data-mp="2arrohimzccgo" />
            </button>}
          {block.type === 'columns' && <button onClick={e => {
          e.stopPropagation();
          setColumnConfigModal({
            isOpen: true,
            blockId: block.id,
            currentCount: block.columnCount || 2
          });
        }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 text-gray-600" title="Configure Columns" data-id="element-776" data-mp="25soquj9bx8iq">
              <SettingsIcon className="h-3.5 w-3.5" data-id="element-777" data-mp="17x2rn0qpagzv" />
            </button>}
          <button onClick={e => {
          e.stopPropagation();
          duplicateBlock(block.id);
        }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-gray-50 text-gray-600" title="Duplicate" data-id="element-778" data-mp="u1qwis8muoif">
            <CopyIcon className="h-3.5 w-3.5" data-id="element-779" data-mp="1qp5ckoadkjfx" />
          </button>
          <button onClick={e => {
          e.stopPropagation();
          deleteBlock(block.id);
        }} className="p-1.5 bg-white border border-gray-200 rounded-md shadow-sm hover:bg-red-50 text-gray-600" title="Delete" data-id="element-780" data-mp="1cxh0gl04luyy">
            <Trash2Icon className="h-3.5 w-3.5 text-red-500" data-id="element-781" data-mp="2ae5wfy2ea30f" />
          </button>
        </div>
        {/* Section drop zone — render children inline with action buttons */}
        {block.type === 'section' ? <div onDragOver={e => {
        e.preventDefault();
        e.stopPropagation();
      }} onDrop={handleSectionDrop(block.id)} data-id="element-782" data-mp="2fbuv0ayl3apz">
            {renderSectionCanvasHeader(block, theme)}
          </div> : block.type === 'columns' ? <div data-id="element-783" data-mp="21bttctahvz9g">
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 rounded-t-lg" data-id="element-784" data-mp="21clgrgtxuqon">
              <ColumnsIcon className="h-4 w-4 text-blue-500 flex-shrink-0" data-id="element-785" data-mp="1wi8rgq5s6l4z" />
              <span className="text-sm font-semibold flex-1" style={{
            color: theme.headingColor
          }} data-id="element-786" data-mp="1rlqq809nt4xe">
                {block.columnCount || 2} Columns
              </span>
            </div>
            <div className="grid gap-3 p-3" style={{
          gridTemplateColumns: `repeat(${block.columnCount || 2}, 1fr)`
        }} data-id="element-787" data-mp="1dl7kizso2aye">
              {Array.from({
            length: block.columnCount || 2
          }).map((_, colIdx) => {
            var colBlocks = block.columns ? block.columns[colIdx] || [] : [];
            var isOver = dragOverCol && dragOverCol.parentId === block.id && dragOverCol.colIndex === colIdx;
            return <div key={colIdx} onDragOver={handleColumnDragOver(block.id, colIdx)} onDragLeave={() => setDragOverCol(null)} onDrop={handleColumnDrop(block.id, colIdx)} className={`min-h-[60px] border-2 border-dashed rounded-md p-2 transition-colors ${isOver ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`} data-id="element-788" data-mp="3ajnaqe6kxc4h" data-mpi={colIdx}>
                    {colBlocks.length > 0 ? <div className="space-y-1.5" data-id="element-789" data-mp="1l63928dcab4x">
                        {colBlocks.map(child => <div key={child.id} className={`group/child text-xs text-gray-600 bg-white rounded-md px-2.5 py-1.5 border transition-colors cursor-pointer flex items-center gap-1.5 ${selectedChild?.childId === child.id ? 'border-blue-400 bg-blue-50/30' : 'border-gray-200 hover:border-gray-300'}`} onClick={e => {
                  e.stopPropagation();
                  setSelectedChild({
                    parentId: block.id,
                    colIndex,
                    childId: child.id
                  });
                  setSelectedId(null);
                }} data-id="element-790" data-mp="1g79t89aky81l">
                            <span className="flex-1 truncate" data-id="element-791" data-mp="2hwdidffzi6kj">
                              {child.label || child.type}
                            </span>
                            <span className="text-[9px] text-gray-400 bg-gray-50 px-1 py-0.5 rounded flex-shrink-0" data-id="element-792" data-mp="31iov3qq2dzzq">
                              {child.type}
                            </span>
                            <button onClick={e => {
                    e.stopPropagation();
                    deleteChildBlock(block.id, colIdx, child.id);
                  }} className="p-0.5 rounded hover:bg-red-100 transition-colors opacity-0 group-hover/child:opacity-100 flex-shrink-0" title="Remove" data-id="element-793" data-mp="2rjg4n7uer3i4">
                              <XIcon className="h-3 w-3 text-red-400" data-id="element-794" data-mp="2drruha3pyp3s" />
                            </button>
                          </div>)}
                      </div> : <div className="flex items-center justify-center h-full text-[10px] text-gray-400" data-id="element-795" data-mp="23tc4xbkcbnwe">
                        Drop here
                      </div>}
                  </div>;
          })}
            </div>
          </div> : renderBlockPreview(block, theme)}
      </div>;
  };
  return <>
      <div className="flex h-[calc(100vh-220px)] min-h-[500px] border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-796" data-mp="p1jbd8ya37b7">
        {/* Left Panel — Block Palette */}
        <div className="w-60 flex-shrink-0 border-r border-gray-200 bg-gray-50 overflow-y-auto" data-id="element-797" data-mp="1hqyygx37wqnh">
          <div className="p-3 border-b border-gray-200" data-id="element-798" data-mp="1hstbzqc5tghj">
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider" data-id="element-799" data-mp="13ysf724rq3jt">
              Blocks
            </h3>
          </div>
          {categories.map(cat => <div key={cat} data-id="element-800" data-mp="1vpovydty8scd">
              <button onClick={() => toggleCat(cat)} className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wider hover:bg-gray-100" data-id="element-801" data-mp="1vpghihi6hez2">
                {cat}
                <ChevronRightIcon className={`h-3 w-3 transition-transform ${collapsedCats.has(cat) ? '' : 'rotate-90'}`} data-id="element-802" data-mp="3g9k5rkyn1h6n" />
              </button>
              {!collapsedCats.has(cat) && <div className="px-2 pb-2 space-y-1" data-id="element-803" data-mp="3pz3rcydqjp57">
                  {activePalette.filter(p => p.category === cat).map(item => {
              const Icon = ICONS[item.icon] || TypeIcon;
              return <div key={item.type} draggable onDragStart={handlePaletteDragStart(item.type as FormBlockType)} onDragEnd={handleDragEnd} className="flex items-center gap-2 px-2.5 py-2 rounded-md bg-white border border-gray-200 text-sm text-gray-700 cursor-grab hover:border-blue-300 hover:shadow-sm active:cursor-grabbing select-none" data-id="element-804" data-mp="3c88d16kgwfu8">
                          <Icon className="h-4 w-4 text-gray-400 flex-shrink-0" data-id="element-805" data-mp="13j983jwmtjjs" />
                          <span className="truncate" data-id="element-806" data-mp="13h6dsnog5499">{item.label}</span>
                        </div>;
            })}
                </div>}
            </div>)}
        </div>

        {/* Center — Canvas */}
        <div className="flex-1 overflow-y-auto bg-gray-100 p-6" onClick={() => setSelectedId(null)} data-id="element-807" data-mp="fuq0l6lepasr">
          <div className="flex items-center justify-between mb-4" data-id="element-808" data-mp="krghtx8y6cfr">
            <div className="flex items-center gap-1" data-id="element-809" data-mp="pkjbe5dq5lvy">
              <button onClick={undo} disabled={historyIdx <= 0} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Undo (Ctrl+Z)" data-id="element-810" data-mp="3o9hv4lx6ofdb">
                <UndoIcon className="h-4 w-4 text-gray-500" data-id="element-811" data-mp="2adqa04j3j317" />
              </button>
              <button onClick={redo} disabled={historyIdx >= history.length - 1} className="p-1.5 rounded hover:bg-gray-200 disabled:opacity-30" title="Redo (Ctrl+Shift+Z)" data-id="element-812" data-mp="26c4su6zj42l7">
                <RedoIcon className="h-4 w-4 text-gray-500" data-id="element-813" data-mp="1wdfir0dvjmfi" />
              </button>
            </div>

            {/* AI Beautify + Logic Rules */}
            <div className="flex items-center gap-2" data-id="element-814" data-mp="1mncge9kv4w4i">
              {/* Logic Rules button — hidden for landing pages */}
              {!hideLogicRules && <button onClick={() => setShowLogicEditor(true)} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 transition-colors" data-id="element-815" data-mp="1ik6e1d0vfp3b">
                  <ZapIcon className="h-3.5 w-3.5" data-id="element-816" data-mp="13r349rtiufn5" />
                  Logic Rules
                  {rules.length > 0 && <span className="ml-0.5 text-[10px] font-semibold bg-purple-200 text-purple-800 px-1.5 py-0.5 rounded-full" data-id="element-817" data-mp="38q2vw77wtltm">
                      {rules.length}
                    </span>}
                </button>}

              {showBeautifyInput && <div className="flex items-center gap-1.5 bg-white border border-purple-200 rounded-lg px-2 py-1 shadow-sm" data-id="element-818" data-mp="2l0rs0gv9jbb9">
                  <input type="text" value={beautifyPrompt} onChange={e => setBeautifyPrompt(e.target.value)} onKeyDown={e => {
                if (e.key === 'Enter') handleBeautify(beautifyPrompt);
                if (e.key === 'Escape') {
                  setShowBeautifyInput(false);
                  setBeautifyPrompt('');
                }
              }} placeholder="e.g. dark mode, playful, corporate..." className="text-xs border-none outline-none bg-transparent w-48 placeholder:text-gray-400" autoFocus data-id="element-819" data-mp="2q04nfq079qpg" />
                  <button onClick={() => handleBeautify(beautifyPrompt)} disabled={isBeautifying} className="p-1 text-purple-600 hover:text-purple-800 disabled:opacity-50" data-id="element-820" data-mp="28j7g6xysk4e0">
                    <SendIcon className="h-3.5 w-3.5" data-id="element-821" data-mp="2dh91lpjowfsn" />
                  </button>
                  <button onClick={() => {
                setShowBeautifyInput(false);
                setBeautifyPrompt('');
              }} className="p-1 text-gray-400 hover:text-gray-600" data-id="element-822" data-mp="2djbnbxdt7mrk">
                    <XIcon className="h-3.5 w-3.5" data-id="element-823" data-mp="x3sbf80cc88c" />
                  </button>
                </div>}
              <button onClick={() => {
              if (showBeautifyInput) handleBeautify(beautifyPrompt);else if (blocks.length > 0) setShowBeautifyInput(true);
            }} disabled={isBeautifying || blocks.length === 0} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 border border-purple-200 rounded-lg hover:bg-purple-100 disabled:opacity-50 transition-colors" title="Use AI to beautify your form's styling" data-id="element-824" data-mp="s5qt4paqocxx">
                {isBeautifying ? <Loader2Icon className="h-3.5 w-3.5 animate-spin" data-id="element-825" data-mp="3g205cp66v2n8" /> : <WandSparklesIcon className="h-3.5 w-3.5" data-id="element-826" data-mp="3kwxkmdu2a88k" />}
                {isBeautifying ? 'Beautifying...' : '✨ AI Beautify'}
              </button>
              <span className="text-xs text-gray-400" data-id="element-827" data-mp="3twu29vi1a6u4">
                {blocks.length} block{blocks.length !== 1 ? 's' : ''}
              </span>
            </div>
          </div>

          <div ref={canvasRef} className="max-w-xl mx-auto rounded-lg min-h-[300px]" style={{
          fontFamily: theme.fontFamily,
          background: theme.backgroundType === 'gradient' ? `linear-gradient(${theme.gradientDirection || 'to bottom right'}, ${theme.gradientFrom || '#667eea'}, ${theme.gradientTo || '#764ba2'})` : theme.backgroundColor || '#ffffff',
          boxShadow: theme.containerShadow === 'lg' ? '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)' : theme.containerShadow === 'md' ? '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)' : theme.containerShadow === 'sm' ? '0 1px 2px 0 rgba(0,0,0,0.05)' : '0 1px 3px 0 rgba(0,0,0,0.1)',
          border: theme.containerBorder ? `1px solid ${theme.inputBorderColor || '#e5e7eb'}` : '1px solid #e5e7eb',
          borderRadius: (theme.borderRadius || 6) + 4
        }} data-id="element-828" data-mp="ctesdjqvdygn">
            <div style={{
            padding: theme.formPadding || 32
          }} data-id="element-829" data-mp="1xzz90vlhz2y8">
              {blocks.length === 0 && <div onDragOver={e => {
              e.preventDefault();
              setDragOverIdx(0);
            }} onDragLeave={handleDragLeave} onDrop={handleCanvasDropEnd} className={`border-2 border-dashed rounded-lg py-20 px-8 text-center transition-colors ${dragOverIdx === 0 ? 'border-blue-400 bg-blue-50' : 'border-gray-300'}`} data-id="element-830" data-mp="1o8uwe2janu6k">
                  <p className="text-sm text-gray-400" data-id="element-831" data-mp="qkuj77sth7jk">
                    Drag blocks here to start building your form
                  </p>
                </div>}

              {canvasRows.map((row, rowIdx) => {
              // Get the flat index of the first block in this row for the drop zone above
              const firstFlatIdx = row.blocks[0].flatIndex;
              if (row.type === 'half-row') {
                return <Fragment key={row.blocks.map(b => b.block.id).join('-')} data-id="element-832" data-mp="3irbwg85eg6cl">
                      {/* Drop zone above the row */}
                      <div onDragOver={handleDragOver(firstFlatIdx)} onDragLeave={handleDragLeave} onDrop={handleDrop(firstFlatIdx)} className="relative transition-all" style={{
                    height: isDragging ? 16 : 6,
                    marginTop: isDragging ? 2 : 0,
                    marginBottom: isDragging ? 2 : 0
                  }} data-id="element-833" data-mp="2r0pb4zk18gx4">
                        {dragOverIdx === firstFlatIdx && <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full shadow-sm" data-id="element-834" data-mp="10dukhqcu7yqh">
                            <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-835" data-mp="vmme736t0thj" />
                            <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-836" data-mp="rmc6nm3d5v0k" />
                          </div>}
                      </div>
                      {/* Two-column grid row */}
                      <div className="grid grid-cols-2 gap-3" style={{
                    marginBottom: (theme.fieldSpacing || 20) - 16
                  }} data-id="element-837" data-mp="1fbemqpicnfsy">
                        {row.blocks.map(item => renderBlockCard(item.block, item.flatIndex))}
                        {/* If only one half-width block, show empty slot */}
                        {row.blocks.length === 1 && <div onDragOver={e => {
                      e.preventDefault();
                      setDragOverIdx(row.blocks[0].flatIndex + 1);
                    }} onDragLeave={handleDragLeave} onDrop={handleDrop(row.blocks[0].flatIndex + 1)} className={`border-2 border-dashed rounded-md p-6 flex items-center justify-center text-xs text-gray-400 transition-colors ${dragOverIdx === row.blocks[0].flatIndex + 1 ? 'border-blue-400 bg-blue-50' : 'border-gray-200'}`} data-id="element-838" data-mp="3g0aq35qgwwkg">
                            Drop a half-width field here
                          </div>}
                      </div>
                    </Fragment>;
              }
              // Full-width block
              const item = row.blocks[0];
              return <Fragment key={item.block.id} data-id="element-839" data-mp="2b8aztjn5cunb" data-mpi={rowIdx}>
                    <div onDragOver={handleDragOver(item.flatIndex)} onDragLeave={handleDragLeave} onDrop={handleDrop(item.flatIndex)} className="relative transition-all" style={{
                  height: isDragging ? 16 : 6,
                  marginTop: isDragging ? 2 : 0,
                  marginBottom: isDragging ? 2 : 0
                }} data-id="element-840" data-mp="2b92khzf7h7yt">
                      {dragOverIdx === item.flatIndex && <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full shadow-sm" data-id="element-841" data-mp="33xhv89rb144c">
                          <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-842" data-mp="2yywxcgziywj2" />
                          <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-843" data-mp="3cxwk1j3h91cl" />
                        </div>}
                    </div>
                    <div style={{
                  marginBottom: (theme.fieldSpacing || 20) - 16
                }} data-id="element-844" data-mp="3rl4x6pgcjgr7">
                      {renderBlockCard(item.block, item.flatIndex)}
                    </div>
                  </Fragment>;
            })}

              {blocks.length > 0 && <div onDragOver={e => {
              e.preventDefault();
              setDragOverIdx(blocks.length);
            }} onDragLeave={handleDragLeave} onDrop={handleDrop(blocks.length)} className="relative transition-all" style={{
              height: isDragging ? 40 : 16
            }} data-id="element-845" data-mp="38ssx69d60rk8">
                  {dragOverIdx === blocks.length && <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-1 bg-blue-500 rounded-full shadow-sm" data-id="element-846" data-mp="2ut1f9xjj2r0y">
                      <div className="absolute -left-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-847" data-mp="11l5oqfp8our" />
                      <div className="absolute -right-1 top-1/2 -translate-y-1/2 w-2.5 h-2.5 bg-blue-500 rounded-full" data-id="element-848" data-mp="3t805oj381t11" />
                    </div>}
                </div>}
            </div>
          </div>
        </div>

        {/* Right Panel — Settings + Properties */}
        <div className="w-72 flex-shrink-0 border-l border-gray-200 bg-white flex flex-col" data-id="element-849" data-mp="3eewrmlou9xf2">
          {settingsSidebar && <div className="border-b border-gray-200 p-3 flex-shrink-0" data-id="element-850" data-mp="35g1ltappipca">
              {settingsSidebar}
            </div>}
          <div className="border-b border-gray-200 px-4 py-2" data-id="element-851" data-mp="30ijibzugl2bo">
            <span className="text-xs font-medium text-gray-900" data-id="element-852" data-mp="13ooigq3e0pic">Design</span>
          </div>
          <div className="flex-1 overflow-y-auto" data-id="element-853" data-mp="yq4rgb9xsnj4">
            {selectedBlock ? <BlockProperties block={selectedBlock} onChange={updates => updateBlock(selectedBlock.id, updates)} onBlur={commitToHistory} onDelete={() => deleteBlock(selectedBlock.id)} hideLogicRules={hideLogicRules} data-id="element-854" data-mp="yo0s86mzgcp6" /> : <ThemeProperties theme={theme} onChange={setTheme} data-id="element-855" data-mp="1kyjxpltt60cw" />}
          </div>
        </div>
      </div>

      {/* Full-screen Logic Rules Editor overlay */}
      {showLogicEditor && <LogicRulesEditor blocks={blocks} rules={rules} onChange={newRules => {
      setRules(newRules);
      onSchemaChange(blocks, theme, newRules);
    }} onClose={() => setShowLogicEditor(false)} data-id="element-856" data-mp="s8tkdf0032hu" />}

      {/* Column Configuration Modal */}
      {columnConfigModal && columnConfigModal.isOpen && <div className="fixed inset-0 z-50 overflow-y-auto" data-id="element-857" data-mp="2cp6kyzyqjzv0">
          <div className="flex min-h-screen items-center justify-center p-4" data-id="element-858" data-mp="2cnn6newn3yvv">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setColumnConfigModal(null)} data-id="element-859" data-mp="djdn0sjdicl9" />
            <div className="relative bg-white rounded-lg shadow-xl w-full max-w-sm p-6" data-id="element-860" data-mp="mlmsv53p5hsb">
              <div className="flex items-center justify-between mb-4" data-id="element-861" data-mp="hls3x73hf29w">
                <h3 className="text-lg font-medium text-gray-900" data-id="element-862" data-mp="hkh0x9hv1yju">
                  Configure Columns
                </h3>
                <button onClick={() => setColumnConfigModal(null)} className="text-gray-400 hover:text-gray-500" data-id="element-863" data-mp="3m11m4f1i7438">
                  <XIcon className="h-5 w-5" data-id="element-864" data-mp="3od91mugm2aj" />
                </button>
              </div>
              <div className="space-y-4" data-id="element-865" data-mp="3uz3hof2gx4j4">
                <div data-id="element-866" data-mp="37b3qxf55pe4j">
                  <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-867" data-mp="37aav397qk916">
                    Number of Columns
                  </label>
                  <div className="flex gap-2" data-id="element-868" data-mp="32aoeqqwm2co5">
                    {[2, 3, 4].map(num => <button key={num} onClick={() => {
                  const block = blocks.find(b => b.id === columnConfigModal.blockId);
                  if (block) {
                    // Ensure we have enough column arrays
                    const currentCols = block.columns || [[], []];
                    const newCols = Array(num).fill(null).map((_, i) => currentCols[i] || []);
                    updateBlock(block.id, {
                      columnCount: num,
                      columns: newCols
                    });
                  }
                  setColumnConfigModal(null);
                }} className={`flex-1 py-2 px-4 border rounded-md text-sm font-medium transition-colors ${columnConfigModal.currentCount === num ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`} data-id="element-869" data-mp="1hsnclwe6njg0">
                        {num}
                      </button>)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* Section Settings Modal */}
      {sectionSettingsBlock && <SectionSettingsModal block={sectionSettingsBlock} onSave={updates => {
      updateBlock(sectionSettingsBlock.id, updates);
      commitToHistory();
      setSectionSettingsBlock(null);
    }} onClose={() => setSectionSettingsBlock(null)} data-id="element-870" data-mp="1qixcpvn8w0vw" />}
    </>;
}
// ── Block Properties Panel ──
function BlockProperties(props: {
  block: FormBlock;
  onChange: (updates: Partial<FormBlock>) => void;
  onBlur: () => void;
  onDelete: () => void;
  hideLogicRules?: boolean;
}) {
  var block = props.block;
  var onChange = props.onChange;
  var onBlur = props.onBlur;
  var onDelete = props.onDelete;
  var hideLogicRules = props.hideLogicRules;
  const isInput = !['heading', 'paragraph', 'divider', 'spacer', 'image', 'submit', 'hidden', 'consent', 'section', 'subform'].includes(block.type);
  const hasOptions = ['select', 'radio', 'checkbox'].includes(block.type);
  return <div className="p-4 space-y-4" data-id="element-871" data-mp="34qwoh4gzhecp">
      <div className="flex items-center justify-between pb-3 border-b border-gray-200" data-id="element-872" data-mp="34ptzj2381lzf">
        <h3 className="text-sm font-semibold text-gray-900 capitalize" data-id="element-873" data-mp="39iwmgrdhtx6n">
          {block.type === 'consent' ? 'Consent / Opt-in' : block.type.replace('_', ' ')}{' '}
          Properties
        </h3>
        <button onClick={onDelete} className="p-1 text-red-500 hover:bg-red-50 rounded" title="Delete block" data-id="element-874" data-mp="3jhvxgglqe4wt">
          <Trash2Icon className="h-4 w-4" data-id="element-875" data-mp="3q1s0flmbkbeb" />
        </button>
      </div>

      {(block.type === 'heading' || block.type === 'paragraph') && <>
          <PropField label="Content" data-id="element-876" data-mp="3v2g2d2c36cnb">
            <textarea value={block.content || ''} onChange={e => onChange({
          content: e.target.value
        })} onBlur={onBlur} rows={3} className="prop-input" data-id="element-877" data-mp="3tioos5bw66w" />
          </PropField>
          {block.type === 'heading' && <PropField label="Level" data-id="element-878" data-mp="3c7r757e4zglc">
              <select value={block.level || 2} onChange={e => {
          onChange({
            level: Number(e.target.value) as 1 | 2 | 3 | 4
          });
          onBlur();
        }} className="prop-input" data-id="element-879" data-mp="3h8f387h82nmf">
                <option value={1} data-id="element-880" data-mp="2ticdhy0s5tqz">H1</option>
                <option value={2} data-id="element-881" data-mp="2th18juvlmfya">H2</option>
                <option value={3} data-id="element-882" data-mp="1an84csk354ap">H3</option>
                <option value={4} data-id="element-883" data-mp="1am5cahb10vt7">H4</option>
              </select>
            </PropField>}
          <PropField label="Text Align" data-id="element-884" data-mp="16kjx2pb0s5fu">
            <div className="flex gap-1" data-id="element-885" data-mp="11ms3lquq8b9j">
              {(['left', 'center', 'right'] as const).map(align => <button key={align} onClick={() => {
            onChange({
              textAlign: align
            });
            onBlur();
          }} className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border transition-colors ${(block.textAlign || 'left') === align ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-886" data-mp="wnf7sglvntut">
                  {align.charAt(0).toUpperCase() + align.slice(1)}
                </button>)}
            </div>
          </PropField>
        </>}

      {block.type === 'spacer' && <PropField label="Height (px)" data-id="element-887" data-mp="3qgtfkjett3c8">
          <input type="number" value={block.height || 24} onChange={e => onChange({
        height: Number(e.target.value)
      })} onBlur={onBlur} min={4} max={200} className="prop-input" data-id="element-888" data-mp="3qjxsc56cx7ei" />
        </PropField>}

      {block.type === 'image' && <ImageBlockProperties block={block} onChange={onChange} onBlur={onBlur} data-id="element-889" data-mp="d514fz8kpdmb" />}

      {block.type === 'video_embed' && <>
          <PropField label="Video URL (YouTube/Vimeo)" data-id="element-890" data-mp="4a4ngkim2wnw">
            <input type="url" value={block.videoUrl || ''} onChange={e => onChange({
          videoUrl: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="https://..." data-id="element-891" data-mp="8b6p18003kt1" />
          </PropField>
          <PropField label="Aspect Ratio" data-id="element-892" data-mp="2gjl9h1b7oyik">
            <select value={block.videoAspectRatio || '16:9'} onChange={e => {
          onChange({
            videoAspectRatio: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-893" data-mp="2gijzl05xmi2f">
              <option value="16:9" data-id="element-894" data-mp="22klkfsjsehlv">16:9 Widescreen</option>
              <option value="4:3" data-id="element-895" data-mp="1xkz6fgw4wxc0">4:3 Standard</option>
              <option value="1:1" data-id="element-896" data-mp="1xmaehswm4r8x">1:1 Square</option>
              <option value="9:16" data-id="element-897" data-mp="2cij8aexqicdy">9:16 Vertical</option>
            </select>
          </PropField>
          <div className="space-y-2 pt-2" data-id="element-898" data-mp="27idotm1mbw6y">
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-899" data-mp="27hc2glf5jn9x">
              <input type="checkbox" checked={block.videoAutoplay || false} onChange={e => {
            onChange({
              videoAutoplay: e.target.checked
            });
            onBlur();
          }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-900" data-mp="3nw3vn0k4gyka" />
              Autoplay
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-901" data-mp="21jr8drxugc4a">
              <input type="checkbox" checked={block.videoMuted ?? true} onChange={e => {
            onChange({
              videoMuted: e.target.checked
            });
            onBlur();
          }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-902" data-mp="26iasrnwshfuj" />
              Muted
            </label>
            <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-903" data-mp="2p91zsitb0u5r">
              <input type="checkbox" checked={block.videoLoop || false} onChange={e => {
            onChange({
              videoLoop: e.target.checked
            });
            onBlur();
          }} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-904" data-mp="2u855m5h3z4es" />
              Loop
            </label>
          </div>
        </>}

      {block.type === 'recaptcha' && <>
          <PropField label="Site Key" data-id="element-905" data-mp="2vbnfib0la3xd">
            <input type="text" value={block.recaptchaSiteKey || ''} onChange={e => onChange({
          recaptchaSiteKey: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Google reCAPTCHA v2 Site Key" data-id="element-906" data-mp="3s0cuntku0ifn" />
          </PropField>
          <PropField label="Theme" data-id="element-907" data-mp="elgmcvzj6x8k">
            <select value={block.recaptchaTheme || 'light'} onChange={e => {
          onChange({
            recaptchaTheme: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-908" data-mp="emhxf0gjrjqa">
              <option value="light" data-id="element-909" data-mp="290lxspqphrg3">Light</option>
              <option value="dark" data-id="element-910" data-mp="1uciwhtnlz3xm">Dark</option>
            </select>
          </PropField>
          <PropField label="Size" data-id="element-911" data-mp="1zbc5b8rya6rg">
            <select value={block.recaptchaSize || 'normal'} onChange={e => {
          onChange({
            recaptchaSize: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-912" data-mp="1zaamuj8luhzp">
              <option value="normal" data-id="element-913" data-mp="1beooniy7uygt">Normal</option>
              <option value="compact" data-id="element-914" data-mp="1gchto8ns9mfi">Compact</option>
            </select>
          </PropField>
        </>}

      {block.type === 'submit' && <>
          {hideLogicRules && <>
              <PropField label="Button Action" data-id="element-915" data-mp="3aq92j5yag29w">
                <select value={block.buttonAction || (hideLogicRules ? 'link' : 'submit')} onChange={e => {
            onChange({
              buttonAction: e.target.value as any
            });
            onBlur();
          }} className="prop-input" data-id="element-916" data-mp="3apzazdxlhn23">
                  <option value="submit" data-id="element-917" data-mp="140xgv2lbvjt5">Submit Form</option>
                  <option value="link" data-id="element-918" data-mp="1hsluxbf50icr">Link to URL</option>
                </select>
              </PropField>
              {(block.buttonAction || 'link') === 'link' && <>
                  <PropField label="Button URL" data-id="element-919" data-mp="1rs30lsue2w8q">
                    <input type="text" value={block.buttonUrl || ''} onChange={e => onChange({
              buttonUrl: e.target.value
            })} onBlur={onBlur} className="prop-input" placeholder="https://example.com" data-id="element-920" data-mp="1rpqhvdgt4hum" />
                  </PropField>
                  <PropField label="Open in new tab" data-id="element-921" data-mp="37ofss807ihr8">
                    <label className="flex items-center gap-2 cursor-pointer" data-id="element-922" data-mp="3cg79zrxh3b4p">
                      <input type="checkbox" checked={block.buttonTarget === '_blank'} onChange={e => onChange({
                buttonTarget: e.target.checked ? '_blank' : '_self'
              })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-923" data-mp="3hftwyt33s5qp" />
                      <span className="text-xs text-gray-600" data-id="element-924" data-mp="2yqnb4x70iy0o">
                        Open link in a new browser tab
                      </span>
                    </label>
                  </PropField>
                </>}
            </>}
          <PropField label="Button Text" data-id="element-925" data-mp="1yhf9kzp0mm2q">
            <input type="text" value={block.buttonText || ''} onChange={e => onChange({
          buttonText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-926" data-mp="1ygclezj2uxqi" />
          </PropField>
          <PropField label="Button Color" data-id="element-927" data-mp="1eswpgwiv9yuw">
            <ColorInput value={block.buttonColor || ''} onChange={v => onChange({
          buttonColor: v
        })} data-id="element-928" data-mp="1ety04zvz0jc3" />
            <p className="text-[10px] text-gray-400 mt-1" data-id="element-929" data-mp="15t04p5zllk54">
              Leave empty to use theme primary color
            </p>
          </PropField>
          <PropField label="Alignment" data-id="element-930" data-mp="30697yg1jw9ex">
            <select value={block.buttonAlign || 'full'} onChange={e => {
          onChange({
            buttonAlign: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-931" data-mp="3056fw4shs0xf">
              <option value="left" data-id="element-932" data-mp="3ghfuybmcm8ys">Left</option>
              <option value="center" data-id="element-933" data-mp="3v758757pu3ff">Center</option>
              <option value="right" data-id="element-934" data-mp="3v5ty6nnofzfv">Right</option>
              <option value="full" data-id="element-935" data-mp="3q8ahlh3ibakr">Full Width</option>
            </select>
          </PropField>
          <PropField label="Position Behavior" data-id="element-936" data-mp="32ig899iec459">
            <select value={block.buttonPosition || 'static'} onChange={e => {
          onChange({
            buttonPosition: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-937" data-mp="32j94vhnn5d9o">
              <option value="static" data-id="element-938" data-mp="37fzjs1npm2ti">Default (flows with form)</option>
              <option value="sticky" data-id="element-939" data-mp="qs5fzkuqo8rs">Sticky (stays visible on scroll)</option>
              <option value="fixed" data-id="element-940" data-mp="mrlk0klwjb78">Fixed (always at bottom of screen)</option>
            </select>
          </PropField>
          {(block.buttonPosition === 'sticky' || block.buttonPosition === 'fixed') && <PropField label="Bottom Offset (px)" data-id="element-941" data-mp="za7eat1py6w8">
              <div className="flex items-center gap-2" data-id="element-942" data-mp="z7uro2l0n8cu">
                <input type="range" min={0} max={60} value={block.buttonStickyOffset || 0} onChange={e => onChange({
            buttonStickyOffset: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-943" data-mp="kdq5o9kwz6dd" />
                <span className="text-xs text-gray-500 w-8 text-right" data-id="element-944" data-mp="13b98uwkoxurv">
                  {block.buttonStickyOffset || 0}px
                </span>
              </div>
            </PropField>}
          {block.buttonPosition && block.buttonPosition !== 'static' && <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-id="element-945" data-mp="20roo7cnt180b">
              <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-946" data-mp="2alu9qgfgio1d">
                <strong data-id="element-947" data-mp="25o27otl3lljg">
                  {block.buttonPosition === 'sticky' ? 'Sticky' : 'Fixed'}:
                </strong>{' '}
                {block.buttonPosition === 'sticky' ? 'Button stays visible at the bottom while scrolling through the form.' : 'Button is pinned to the bottom of the viewport at all times.'}
              </p>
            </div>}
        </>}

      {block.type === 'consent' && <>
          <PropField label="Consent Text" data-id="element-948" data-mp="1mv84ewwu7hv2">
            <textarea value={block.consentText || ''} onChange={e => onChange({
          consentText: e.target.value
        })} onBlur={onBlur} rows={3} className="prop-input" placeholder="I agree to receive updates and communications" data-id="element-949" data-mp="1hxgbq0odbrpt" />
          </PropField>
          <PropField label="Field Name" data-id="element-950" data-mp="3fkquaqjeytwz">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-951" data-mp="3fnwi82gb3pr4" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-952" data-mp="2wvkkjt0llcnb">
            <span className="text-xs font-medium text-gray-700" data-id="element-953" data-mp="2wt7s2lx7rjw0">Required</span>
            <button onClick={() => {
          onChange({
            required: !block.required
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.required ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-954" data-mp="31ux5g87ws9n9">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.required ? 'translate-x-4' : ''}`} data-id="element-955" data-mp="2e2qdrmd4oqu6" />
            </button>
          </div>
          <PropField label="Help Text" data-id="element-956" data-mp="1u2yie8gf1yhk">
            <input type="text" value={block.helpText} onChange={e => onChange({
          helpText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-957" data-mp="1u417cau6hquu" />
          </PropField>
        </>}

      {block.type === 'section' && <>
          <PropField label="Section Title" data-id="element-958" data-mp="2cs6abg30h7sl">
            <input type="text" value={block.sectionTitle || ''} onChange={e => onChange({
          sectionTitle: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Section Title" data-id="element-959" data-mp="2cvk9ygkaj2wc" />
          </PropField>
          <PropField label="Field Name" data-id="element-960" data-mp="sf78ktdqe1i4">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-961" data-mp="340ybwe3h4uqf" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-962" data-mp="2z8y6450q37lx">
            <span className="text-xs font-medium text-gray-700" data-id="element-963" data-mp="2lagkccudo56n">
              Show Title
            </span>
            <button onClick={() => {
          onChange({
            sectionShowTitle: !(block.sectionShowTitle !== false)
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.sectionShowTitle !== false ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-964" data-mp="2pcbk3cwnuaib">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.sectionShowTitle !== false ? 'translate-x-4' : ''}`} data-id="element-965" data-mp="9iutd2s4ts8x" />
            </button>
          </div>
          <PropField label="Animation" data-id="element-966" data-mp="838tfb2as0qg">
            <select value={block.sectionAnimation || 'none'} onChange={e => {
          onChange({
            sectionAnimation: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-967" data-mp="827akkf1i9y6">
              <option value="none" data-id="element-968" data-mp="vv5ss2ok90ao">None</option>
              <option value="fadeIn" data-id="element-969" data-mp="qtqagidrp7vz">Fade In</option>
              <option value="slideLeft" data-id="element-970" data-mp="qwcggdl0ffc3">Slide from Left</option>
              <option value="slideRight" data-id="element-971" data-mp="m1ys2v4rm8gx">Slide from Right</option>
              <option value="slideUp" data-id="element-972" data-mp="h4q8cq3yxwdk">Slide from Bottom</option>
              <option value="slideDown" data-id="element-973" data-mp="h241ytstdmwx">Slide from Top</option>
              <option value="zoomIn" data-id="element-974" data-mp="1ekcii9nrus5z">Zoom In</option>
            </select>
          </PropField>
          {block.children && block.children.length > 0 && <div className="pt-2 border-t border-gray-200" data-id="element-975" data-mp="1i1i54i45rzug">
              <p className="text-[10px] text-gray-500 font-medium mb-1" data-id="element-976" data-mp="1i3balu31ce0v">
                {block.children.length} field
                {block.children.length !== 1 ? 's' : ''} in this section
              </p>
            </div>}
          <div className="pt-2 border-t border-gray-200" data-id="element-977" data-mp="1w19jwl2i1k9i">
            <p className="text-[10px] text-gray-400" data-id="element-978" data-mp="1w2ueubsq757w">
              Drag fields onto this section to group them. Click the ⚙️ gear
              icon for more settings. Use logic rules to show/hide entire
              sections.
            </p>
          </div>
        </>}

      {block.type === 'subform' && <>
          <PropField label="Subform Name" data-id="element-979" data-mp="2errderv5ojvx">
            <input type="text" value={block.subformName || ''} onChange={e => onChange({
          subformName: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-980" data-mp="2eqpq9p0v86xu" />
          </PropField>
          <PropField label="Field Name" data-id="element-981" data-mp="39z2g6u7h89t7">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-982" data-mp="352bwzy0dky3k" />
          </PropField>
          <div className="flex items-center justify-between py-1" data-id="element-983" data-mp="2qdplk7dvcqhm">
            <span className="text-xs font-medium text-gray-700" data-id="element-984" data-mp="2mbujv1s10b3b">Show Name</span>
            <button onClick={() => {
          onChange({
            subformShowName: !(block.subformShowName !== false)
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.subformShowName !== false ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-985" data-mp="302pm3v80rtxx">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.subformShowName !== false ? 'translate-x-4' : ''}`} data-id="element-986" data-mp="22n1y2drgzs9y" />
            </button>
          </div>
          <div className="grid grid-cols-2 gap-3 pt-1" data-id="element-987" data-mp="94uxn4lx05n8">
            <PropField label="Min Rows" data-id="element-988" data-mp="96fq8ooo5eig">
              <input type="number" value={block.subformMinRows || 1} onChange={e => {
            const val = Math.max(0, Math.min(50, Number(e.target.value)));
            onChange({
              subformMinRows: val
            });
          }} onBlur={onBlur} min={0} max={50} className="prop-input" data-id="element-989" data-mp="e3fvixdp2d8i" />
            </PropField>
            <PropField label="Max Rows" data-id="element-990" data-mp="3clbhk6zkovoi">
              <input type="number" value={block.subformMaxRows || 10} onChange={e => {
            const val = Math.max(1, Math.min(100, Number(e.target.value)));
            onChange({
              subformMaxRows: val
            });
          }} onBlur={onBlur} min={1} max={100} className="prop-input" data-id="element-991" data-mp="3cka27qbixn0z" />
            </PropField>
          </div>

          {/* Subform Fields/Columns */}
          <div className="space-y-4 pt-3 border-t border-gray-200" data-id="element-992" data-mp="1cii14a8eqtr8">
            <div className="flex items-center justify-between" data-id="element-993" data-mp="18kbzfgmff4d6">
              <span className="text-xs font-semibold text-gray-700" data-id="element-994" data-mp="13j4o57l2rn1r">
                Fields
              </span>
              <button onClick={() => {
            const cols = [...(block.subformColumns || [])];
            cols.push({
              label: `Field ${cols.length + 1}`,
              type: 'text',
              required: false,
              placeholder: ''
            });
            onChange({
              subformColumns: cols
            });
            onBlur();
          }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-995" data-mp="ylmfliayz9ua">
                <PlusIcon className="h-3 w-3" data-id="element-996" data-mp="358brdfdkh0pn" /> Add Field
              </button>
            </div>
            {(block.subformColumns || []).map((col, i) => <div key={i} className="bg-gray-50 rounded-lg p-3 space-y-3 border border-gray-200" data-id="element-997" data-mp="3aa13yzgftmfu" data-mpi={i}>
                {/* Row 1: Field number header + delete */}
                <div className="flex items-center justify-between" data-id="element-998" data-mp="2hl2flklq1o23">
                  <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-999" data-mp="2mgravbqpkisl">
                    Field {i + 1}
                  </span>
                  <button onClick={() => {
              const cols = (block.subformColumns || []).filter((_, j) => j !== i);
              onChange({
                subformColumns: cols
              });
              onBlur();
            }} disabled={(block.subformColumns || []).length <= 1} className="p-1 text-gray-400 hover:text-red-500 disabled:opacity-30 rounded hover:bg-red-50 transition-colors" title="Remove field" data-id="element-1000" data-mp="1smswuzsks0x">
                    <XIcon className="h-3.5 w-3.5" data-id="element-1001" data-mp="2uiql0fb0hwdp" />
                  </button>
                </div>
                {/* Row 2: Label — full width */}
                <div data-id="element-1002" data-mp="347ry9xq69znz">
                  <label className="block text-[10px] font-medium text-gray-500 mb-1" data-id="element-1003" data-mp="3480b5pmap4z6">
                    Label
                  </label>
                  <input type="text" value={col.label} onChange={e => {
              const cols = [...(block.subformColumns || [])];
              cols[i] = {
                ...cols[i],
                label: e.target.value
              };
              onChange({
                subformColumns: cols
              });
            }} onBlur={onBlur} className="prop-input w-full" placeholder="Field label" data-id="element-1004" data-mp="2zfqpils7qr2p" />
                </div>
                {/* Row 3: Type + Required */}
                <div className="flex items-end gap-3" data-id="element-1005" data-mp="151cuuwpz7q1e">
                  <div className="flex-1" data-id="element-1006" data-mp="19zo7ff7noenb">
                    <label className="block text-[10px] font-medium text-gray-500 mb-1" data-id="element-1007" data-mp="19zedxhnejpcv">
                      Type
                    </label>
                    <select value={col.type} onChange={e => {
                const cols = [...(block.subformColumns || [])];
                cols[i] = {
                  ...cols[i],
                  type: e.target.value
                };
                onChange({
                  subformColumns: cols
                });
                onBlur();
              }} className="prop-input w-full" data-id="element-1008" data-mp="101qjkstpkjv8">
                      <option value="text" data-id="element-1009" data-mp="3r77wy5lne04b">Text</option>
                      <option value="number" data-id="element-1010" data-mp="3mazcqdslfgqa">Number</option>
                      <option value="email" data-id="element-1011" data-mp="3m8d74jp9jbap">Email</option>
                      <option value="date" data-id="element-1012" data-mp="nr99o0ykdqbh">Date</option>
                      <option value="url" data-id="element-1013" data-mp="itqwtzhfeqy7">URL</option>
                      <option value="textarea" data-id="element-1014" data-mp="iu0qbx1ojg8n">Textarea</option>
                      <option value="select" data-id="element-1015" data-mp="dtmnquiukyi0">Select</option>
                    </select>
                  </div>
                  <label className="flex items-center gap-1.5 text-xs text-gray-600 whitespace-nowrap cursor-pointer pb-1.5" data-id="element-1016" data-mp="9rrff62i1kuq">
                    <input type="checkbox" checked={col.required || false} onChange={e => {
                const cols = [...(block.subformColumns || [])];
                cols[i] = {
                  ...cols[i],
                  required: e.target.checked
                };
                onChange({
                  subformColumns: cols
                });
                onBlur();
              }} className="rounded border-gray-300 text-blue-600 h-3.5 w-3.5" data-id="element-1017" data-mp="2ylybbai6h5ui" />
                    Required
                  </label>
                </div>
                {/* Row 4: Placeholder — full width */}
                <div data-id="element-1018" data-mp="21sor8ejmlelf">
                  <label className="block text-[10px] font-medium text-gray-500 mb-1" data-id="element-1019" data-mp="21q2p4ufi7ral">
                    Placeholder
                  </label>
                  <input type="text" value={col.placeholder || ''} onChange={e => {
              const cols = [...(block.subformColumns || [])];
              cols[i] = {
                ...cols[i],
                placeholder: e.target.value
              };
              onChange({
                subformColumns: cols
              });
            }} onBlur={onBlur} className="prop-input w-full" placeholder="Placeholder text" data-id="element-1020" data-mp="7exnfvnz9oxw" />
                </div>
                {col.type === 'select' && <div className="space-y-1.5 pt-1 border-t border-gray-200" data-id="element-1021" data-mp="1tmup1firyh0z">
                    <span className="text-xs text-gray-500 font-medium" data-id="element-1022" data-mp="27fu8ttplaxec">
                      Options
                    </span>
                    {(col.options || []).map((opt, oi) => <div key={oi} className="flex items-center gap-1.5" data-id="element-1023" data-mp="2hcyu7rbm2ou3" data-mpi={oi}>
                        <input type="text" value={opt.label} onChange={e => {
                const cols = [...(block.subformColumns || [])];
                const opts = [...(cols[i].options || [])];
                opts[oi] = {
                  label: e.target.value,
                  value: e.target.value
                };
                cols[i] = {
                  ...cols[i],
                  options: opts
                };
                onChange({
                  subformColumns: cols
                });
              }} onBlur={onBlur} className="prop-input flex-1 text-xs" placeholder="Option label" data-id="element-1024" data-mp="2hb5pwioh0gp9" />
                        <button onClick={() => {
                const cols = [...(block.subformColumns || [])];
                const opts = (cols[i].options || []).filter((_, j) => j !== oi);
                cols[i] = {
                  ...cols[i],
                  options: opts
                };
                onChange({
                  subformColumns: cols
                });
                onBlur();
              }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-1025" data-mp="1ubn1pcodk25w">
                          <XIcon className="h-3 w-3" data-id="element-1026" data-mp="1m2r2q0m7fctp" />
                        </button>
                      </div>)}
                    <button onClick={() => {
              const cols = [...(block.subformColumns || [])];
              const opts = [...(cols[i].options || [])];
              opts.push({
                label: `Option ${opts.length + 1}`,
                value: `option_${opts.length + 1}`
              });
              cols[i] = {
                ...cols[i],
                options: opts
              };
              onChange({
                subformColumns: cols
              });
              onBlur();
            }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1" data-id="element-1027" data-mp="1h5qv3l9pi20h">
                      <PlusIcon className="h-3 w-3" data-id="element-1028" data-mp="34zvqv1hpdi9g" /> Add Option
                    </button>
                  </div>}
              </div>)}
          </div>

          <div className="pt-3 border-t border-gray-200" data-id="element-1029" data-mp="3etcmmb1eh7fr">
            <p className="text-[10px] text-gray-400 leading-relaxed" data-id="element-1030" data-mp="2hcvvc2l3oyee">
              Subforms allow users to add multiple rows of structured data. Each
              field becomes a column in the table. Subforms cannot be used as
              logic rule triggers but can be shown/hidden by logic rules.
            </p>
          </div>
        </>}

      {block.type === 'hidden' && <>
          <PropField label="Field Name" data-id="element-1031" data-mp="1n1myn825fbqd">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono" data-id="element-1032" data-mp="1n0ln72h80n84" />
          </PropField>
          <PropField label="Value" data-id="element-1033" data-mp="1i3lcufdc8yb7">
            <input type="text" value={block.defaultValue} onChange={e => onChange({
          defaultValue: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-1034" data-mp="13dnnvx7l44ky" />
          </PropField>
        </>}

      {isInput && <>
          <PropField label="Label" data-id="element-1035" data-mp="foz66zql0ha1">
            <input type="text" value={block.label} onChange={e => onChange({
          label: e.target.value,
          name: labelToName(e.target.value) + '_' + block.id.slice(0, 4)
        })} onBlur={onBlur} className="prop-input" data-id="element-1036" data-mp="fqacp7bew34u" />
          </PropField>
          <PropField label="Field Name" data-id="element-1037" data-mp="ymikoivt6s3u">
            <input type="text" value={block.name} onChange={e => onChange({
          name: e.target.value
        })} onBlur={onBlur} className="prop-input font-mono text-xs" data-id="element-1038" data-mp="tpjsi5vpgv8a" />
          </PropField>
          {block.type !== 'radio' && block.type !== 'checkbox' && <PropField label="Placeholder" data-id="element-1039" data-mp="1cg9ttq5mo9z1">
              <input type="text" value={block.placeholder} onChange={e => onChange({
          placeholder: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-1040" data-mp="1chckbwz1gaef" />
            </PropField>}
          <PropField label="Help Text" data-id="element-1041" data-mp="nu08nhqw5plu">
            <input type="text" value={block.helpText} onChange={e => onChange({
          helpText: e.target.value
        })} onBlur={onBlur} className="prop-input" data-id="element-1042" data-mp="nv1jpm7wqc3k" />
          </PropField>
          <div className="flex items-center justify-between" data-id="element-1043" data-mp="12p6hechdg2iv">
            <span className="text-xs font-medium text-gray-700" data-id="element-1044" data-mp="16rb40r9ejyw0">Required</span>
            <button onClick={() => {
          onChange({
            required: !block.required
          });
          onBlur();
        }} className={`relative w-9 h-5 rounded-full transition-colors ${block.required ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1045" data-mp="34j5muh7tbpnn">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${block.required ? 'translate-x-4' : ''}`} data-id="element-1046" data-mp="2w86sgxp0ayt0" />
            </button>
          </div>
          <PropField label="Width" data-id="element-1047" data-mp="2n78k637jc54m">
            <select value={block.width} onChange={e => {
          onChange({
            width: e.target.value as 'full' | 'half'
          });
          onBlur();
        }} className="prop-input" data-id="element-1048" data-mp="3jzu7oicwolfg">
              <option value="full" data-id="element-1049" data-mp="35zt7z68dnk1k">Full Width</option>
              <option value="half" data-id="element-1050" data-mp="35z9sdw6797qp">Half Width (side by side)</option>
            </select>
          </PropField>
        </>}

      {(block.type === 'radio' || block.type === 'checkbox') && <div className="space-y-3 pb-3 border-b border-gray-200" data-id="element-1051" data-mp="1uitazxb34h17">
          <PropField label="Options Layout" data-id="element-1052" data-mp="3igtytrn6u6a8">
            <select value={block.optionsLayout || 'vertical'} onChange={e => {
          onChange({
            optionsLayout: e.target.value as 'vertical' | 'horizontal'
          });
          onBlur();
        }} className="prop-input" data-id="element-1053" data-mp="3iezgml3aws9u">
              <option value="vertical" data-id="element-1054" data-mp="52fk1ix60l7d">Vertical (stacked)</option>
              <option value="horizontal" data-id="element-1055" data-mp="52z538i43ppm">Horizontal (inline)</option>
            </select>
          </PropField>
          {block.optionsLayout !== 'horizontal' && <PropField label="Columns" data-id="element-1056" data-mp="3s5u41ssa9xwv">
              <select value={block.optionsColumns || 1} onChange={e => {
          onChange({
            optionsColumns: Number(e.target.value) as 1 | 2 | 3 | 4
          });
          onBlur();
        }} className="prop-input" data-id="element-1057" data-mp="3s8zrl3l9krqh">
                <option value={1} data-id="element-1058" data-mp="2d99kuhnlo0dt">1 column</option>
                <option value={2} data-id="element-1059" data-mp="2d7zmrsgqys76">2 columns</option>
                <option value={3} data-id="element-1060" data-mp="298quyt59p4e9">3 columns</option>
                <option value={4} data-id="element-1061" data-mp="29aa9ae7d55de">4 columns</option>
              </select>
            </PropField>}
        </div>}

      {hasOptions && <div className="space-y-2" data-id="element-1062" data-mp="2i8m8h4mapbhg">
          <div className="flex items-center justify-between" data-id="element-1063" data-mp="2i9ovv2kesvsm">
            <span className="text-xs font-medium text-gray-700" data-id="element-1064" data-mp="1uijg0negy3ib">Options</span>
            <button onClick={() => {
          const opts = [...(block.validation.options || [])];
          opts.push({
            label: `Option ${opts.length + 1}`,
            value: `option_${opts.length + 1}`
          });
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
          onBlur();
        }} className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-0.5" data-id="element-1065" data-mp="1pkhxc3kyg67d">
              <PlusIcon className="h-3 w-3" data-id="element-1066" data-mp="3but5m1gidf46" /> Add
            </button>
          </div>
          {(block.validation.options || []).map((opt, i) => <div key={i} className="flex items-center gap-1" data-id="element-1067" data-mp="3gmb6yqmg1hpo" data-mpi={i}>
              <input type="text" value={opt.label} onChange={e => {
          const opts = [...(block.validation.options || [])];
          opts[i] = {
            ...opts[i],
            label: e.target.value,
            value: labelToName(e.target.value)
          };
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
        }} onBlur={onBlur} className="prop-input flex-1 text-xs" placeholder="Label" data-id="element-1068" data-mp="3knmpydatb4ow" />
              <button onClick={() => {
          const opts = (block.validation.options || []).filter((_, j) => j !== i);
          onChange({
            validation: {
              ...block.validation,
              options: opts
            }
          });
          onBlur();
        }} className="p-1 text-gray-400 hover:text-red-500" data-id="element-1069" data-mp="r9jnowzbuwbm">
                <XIcon className="h-3 w-3" data-id="element-1070" data-mp="3jz0g1w55cwj" />
              </button>
            </div>)}
        </div>}
    </div>;
}
// ── Image Block Properties ──
function VideoEmbedProperties({
  block,
  onChange,
  onBlur
}: {
  block: FormBlock;
  onChange: (updates: Partial<FormBlock>) => void;
  onBlur: () => void;
}) {
  return <div className="space-y-4 p-4" data-id="element-1071" data-mp="1sxue9pfxfbni">
      <PropField label="Video or GIF URL" data-id="element-1072" data-mp="1oxtrnhiobu5d">
        <input type="url" value={block.videoUrl || ''} onChange={e => onChange({
        videoUrl: e.target.value
      })} onBlur={onBlur} placeholder="https://youtube.com/... or .mp4/.gif" className="prop-input" data-id="element-1073" data-mp="1oyv5ftr2quqs" />
        <p className="text-[10px] text-gray-500 mt-1" data-id="element-1074" data-mp="1yfh4re7v4ane">
          Supports YouTube, Vimeo, MP4, and GIF URLs.
        </p>
      </PropField>

      <PropField label="Aspect Ratio" data-id="element-1075" data-mp="1oiul2sr9vpmy">
        <select value={block.videoAspectRatio || '16:9'} onChange={e => onChange({
        videoAspectRatio: e.target.value as any
      })} onBlur={onBlur} className="prop-input" data-id="element-1076" data-mp="1ohswdlhc34mr">
          <option value="16:9" data-id="element-1077" data-mp="1kik4yn9rniud">16:9 (Widescreen)</option>
          <option value="4:3" data-id="element-1078" data-mp="39wgvw6ih4jfu">4:3 (Standard)</option>
          <option value="1:1" data-id="element-1079" data-mp="39xrxq0sczh4b">1:1 (Square)</option>
          <option value="9:16" data-id="element-1080" data-mp="3evjnop9g0t5v">9:16 (Vertical)</option>
        </select>
      </PropField>

      <div className="space-y-2 pt-2 border-t border-gray-100" data-id="element-1081" data-mp="3et7a0oapx4ym">
        <label className="flex items-center gap-2 px-1 py-1 text-xs text-gray-700 cursor-pointer" data-id="element-1082" data-mp="3p003xjqu4ca7">
          <input type="checkbox" checked={block.videoAutoplay || false} onChange={e => onChange({
          videoAutoplay: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1083" data-mp="3k1ym1255aj0b" />
          Autoplay
        </label>
        <label className="flex items-center gap-2 px-1 py-1 text-xs text-gray-700 cursor-pointer" data-id="element-1084" data-mp="6m0mapiitypc">
          <input type="checkbox" checked={block.videoMuted ?? true} onChange={e => onChange({
          videoMuted: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1085" data-mp="kls8ven3sney" />
          Muted
        </label>
        <label className="flex items-center gap-2 px-1 py-1 text-xs text-gray-700 cursor-pointer" data-id="element-1086" data-mp="2icuvbrk3ecf9">
          <input type="checkbox" checked={block.videoLoop || false} onChange={e => onChange({
          videoLoop: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1087" data-mp="1pn8oftpin2sx" />
          Loop
        </label>
        <p className="text-[10px] text-gray-500 leading-tight" data-id="element-1088" data-mp="16ppb4dw0yy0p">
          Note: Most browsers require videos to be muted to autoplay.
        </p>
      </div>
    </div>;
}
function ImageBlockProperties({
  block,
  onChange,
  onBlur
}: {
  block: FormBlock;
  onChange: (updates: Partial<FormBlock>) => void;
  onBlur: () => void;
}) {
  const {
    currentWorkspace,
    currentSubRegion
  } = useWorkspace();
  const team = currentWorkspace.name.replace(/\s*Team\s*$/i, '');
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaImages, setMediaImages] = useState<MediaLibraryRecord[]>([]);
  const [isLoadingMedia, setIsLoadingMedia] = useState(false);
  const [mediaSearch, setMediaSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'source' | 'layout' | 'effects'>('source');
  const loadMedia = async () => {
    if (mediaImages.length > 0) return;
    setIsLoadingMedia(true);
    try {
      const all = await mediaLibraryApi.list();
      // Filter by team — same logic as MediaLibrary page
      const teamFiltered = all.filter(img => img.Team === team || img.Team === currentWorkspace.name);
      setMediaImages(teamFiltered);
    } catch (err) {
      console.warn('Failed to load media library:', err);
    } finally {
      setIsLoadingMedia(false);
    }
  };
  const filteredMedia = mediaImages.filter(img => {
    if (!mediaSearch) return true;
    const q = mediaSearch.toLowerCase();
    return (img.Image_Name || '').toLowerCase().includes(q) || (img.Category || '').toLowerCase().includes(q);
  });
  const tabs = [{
    id: 'source' as const,
    label: 'Source',
    icon: ImageIcon
  }, {
    id: 'layout' as const,
    label: 'Layout',
    icon: SlidersHorizontalIcon
  }, {
    id: 'effects' as const,
    label: 'Effects',
    icon: SparklesIcon
  }];
  return <>
      {/* Tab bar */}
      <div className="flex gap-1 p-1 bg-gray-100 rounded-lg" data-id="element-1089" data-mp="tu291rk9i0r">
        {tabs.map(t => {
        const Icon = t.icon;
        return <button key={t.id} onClick={() => setActiveTab(t.id)} className={`flex-1 flex items-center justify-center gap-1 py-1.5 text-[10px] font-medium rounded-md transition-colors ${activeTab === t.id ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-1090" data-mp="3s3154xbjhrlt">
              <Icon className="h-3 w-3" data-id="element-1091" data-mp="39ayklavs1r6a" />
              {t.label}
            </button>;
      })}
      </div>

      {activeTab === 'source' && <>
          <PropField label="Image URL" data-id="element-1092" data-mp="1jtjtsw30abgd">
            <input type="url" value={block.src || ''} onChange={e => onChange({
          src: e.target.value
        })} onBlur={onBlur} placeholder="https://..." className="prop-input" data-id="element-1093" data-mp="1jsi69s4szwhr" />
          </PropField>

          {/* Media Library Picker */}
          <div data-id="element-1094" data-mp="1144t52r8foci">
            <button onClick={() => {
          setShowMediaPicker(!showMediaPicker);
          if (!showMediaPicker) loadMedia();
        }} className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors" data-id="element-1095" data-mp="114mxkmo1t8vi">
              <ImagePlusIcon className="h-3.5 w-3.5" data-id="element-1096" data-mp="ibj34z30arzu" />
              Choose from Media Library
            </button>

            {showMediaPicker && <div className="mt-2 border border-gray-200 rounded-lg overflow-hidden bg-white" data-id="element-1097" data-mp="mbt94bqstiep">
                <div className="p-2 border-b border-gray-100" data-id="element-1098" data-mp="377t9tagg56r">
                  <div className="relative" data-id="element-1099" data-mp="3uhexm7lqkhjs">
                    <SearchIcon className="absolute left-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" data-id="element-1100" data-mp="czdodbw072q6" />
                    <input type="text" value={mediaSearch} onChange={e => setMediaSearch(e.target.value)} placeholder="Search images..." className="w-full pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500" data-id="element-1101" data-mp="883z4ceda1et" />
                  </div>
                </div>
                <div className="max-h-48 overflow-y-auto p-2" data-id="element-1102" data-mp="qzoh6kgvirdz">
                  {isLoadingMedia ? <div className="flex items-center justify-center py-6" data-id="element-1103" data-mp="2xpuuinj9u03c">
                      <Loader2Icon className="h-5 w-5 text-gray-400 animate-spin" data-id="element-1104" data-mp="2srtd071hu8tz" />
                    </div> : filteredMedia.length === 0 ? <div className="text-center py-6" data-id="element-1105" data-mp="1d7akz68tk0vf">
                      <ImageIcon className="h-6 w-6 text-gray-300 mx-auto mb-1" data-id="element-1106" data-mp="1d6r29nbch8gc" />
                      <p className="text-[10px] text-gray-400" data-id="element-1107" data-mp="yhuyw3k8gjm2">
                        {mediaSearch ? 'No images match your search' : 'No images in library'}
                      </p>
                    </div> : <div className="grid grid-cols-3 gap-1.5" data-id="element-1108" data-mp="tkmctrvyrvfj">
                      {filteredMedia.map(img => <button key={img.ID} onClick={() => {
                onChange({
                  src: img.Image_URL,
                  alt: img.Image_Name || img.Description || ''
                });
                onBlur();
                setShowMediaPicker(false);
                setMediaSearch('');
              }} className={`relative group rounded-md overflow-hidden border-2 transition-all aspect-square ${block.src === img.Image_URL ? 'border-blue-500 ring-1 ring-blue-500' : 'border-transparent hover:border-blue-300'}`} data-id="element-1109" data-mp="flcxsvjhv7d5">
                          <img src={img.Image_URL} alt={img.Image_Name} className="w-full h-full object-cover" onError={e => {
                  ;
                  (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="40" height="40"><rect fill="%23f3f4f6" width="40" height="40"/><text x="50%" y="50%" text-anchor="middle" dy=".3em" fill="%239ca3af" font-size="10">?</text></svg>';
                }} data-id="element-1110" data-mp="2w68i3t2el2mc" />
                          <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/60 to-transparent p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1111" data-mp="3iffbdq1t34us">
                            <p className="text-[9px] text-white truncate font-medium" data-id="element-1112" data-mp="3rflkkzqh1io5">
                              {img.Image_Name}
                            </p>
                          </div>
                        </button>)}
                    </div>}
                </div>
              </div>}
          </div>

          <PropField label="Alt Text" data-id="element-1113" data-mp="97w1awj3zswj">
            <input type="text" value={block.alt || ''} onChange={e => onChange({
          alt: e.target.value
        })} onBlur={onBlur} className="prop-input" placeholder="Describe the image..." data-id="element-1114" data-mp="dzxc0e0mpbkg" />
          </PropField>

          {/* Preview */}
          {block.src && <div className="rounded-lg border border-gray-200 overflow-hidden" data-id="element-1115" data-mp="1e7lk8wn98rzi">
              <img src={block.src} alt={block.alt || ''} className="w-full h-24 object-contain bg-gray-50" onError={e => {
          ;
          (e.target as HTMLImageElement).style.display = 'none';
        }} data-id="element-1116" data-mp="19a3a52xi46px" />
            </div>}
        </>}

      {activeTab === 'layout' && <>
          <PropField label="Alignment" data-id="element-1117" data-mp="qjbzly1s2a9y">
            <div className="grid grid-cols-3 gap-1" data-id="element-1118" data-mp="qkn6w7ufm05t">
              {(['left', 'center', 'right'] as const).map(align => <button key={align} onClick={() => {
            onChange({
              imageAlign: align
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageAlign || 'center') === align ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1119" data-mp="14cjrlqtrt7s6">
                  {align}
                </button>)}
            </div>
          </PropField>

          <PropField label="Sizing Mode" data-id="element-1120" data-mp="2egupjz8aatfa">
            <select value={block.imageSizing || 'maxWidth'} onChange={e => {
          onChange({
            imageSizing: e.target.value as 'natural' | 'maxWidth' | 'custom'
          });
          onBlur();
        }} className="prop-input" data-id="element-1121" data-mp="2ehwcb0ynx4cu">
              <option value="natural" data-id="element-1122" data-mp="2x69p1p8bnahk">Natural (no constraints)</option>
              <option value="maxWidth" data-id="element-1123" data-mp="1gtcicsu9tzde">Max Width % (responsive)</option>
              <option value="custom" data-id="element-1124" data-mp="1gv5rcescwvok">Custom dimensions</option>
            </select>
          </PropField>

          {(block.imageSizing || 'maxWidth') === 'maxWidth' && <PropField label="Max Width" data-id="element-1125" data-mp="1u88aksxtqgam">
              <div className="flex items-center gap-2" data-id="element-1126" data-mp="1z84d0nqvhzo2">
                <input type="range" min={10} max={100} value={block.imageMaxWidth || 100} onChange={e => onChange({
            imageMaxWidth: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-1127" data-mp="1z6jhaustoaom" />
                <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1128" data-mp="1gfinel6k75bx">
                  {block.imageMaxWidth || 100}%
                </span>
              </div>
            </PropField>}

          {block.imageSizing === 'custom' && <>
              <div className="grid grid-cols-2 gap-3" data-id="element-1129" data-mp="sr93xpwtitny">
                <PropField label="Width (px)" data-id="element-1130" data-mp="xr55zjlygb0v">
                  <input type="number" value={block.imageWidth || ''} onChange={e => onChange({
              imageWidth: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-1131" data-mp="xpu3ro85rbbv" />
                </PropField>
                <PropField label="Height (px)" data-id="element-1132" data-mp="2s3c64trgnrbj">
                  <input type="number" value={block.imageHeight || ''} onChange={e => onChange({
              imageHeight: e.target.value ? Number(e.target.value) : 0
            })} onBlur={onBlur} min={0} placeholder="auto" className="prop-input" data-id="element-1133" data-mp="2nbu4s4lizoq1" />
                </PropField>
              </div>
              <PropField label="Max Width (%)" data-id="element-1134" data-mp="1urfcddn55sqy">
                <div className="flex items-center gap-2" data-id="element-1135" data-mp="1zop798tauwm6">
                  <input type="range" min={0} max={100} value={block.imageMaxWidth || 0} onChange={e => onChange({
              imageMaxWidth: Number(e.target.value)
            })} onMouseUp={onBlur} className="flex-1" data-id="element-1136" data-mp="1zm2r4kscf8sc" />
                  <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1137" data-mp="24mh0c65okory">
                    {block.imageMaxWidth || 0 ? `${block.imageMaxWidth}%` : 'none'}
                  </span>
                </div>
              </PropField>
            </>}

          {(block.imageSizing === 'custom' || block.imageSizing === 'maxWidth') && <PropField label="Max Height (px)" data-id="element-1138" data-mp="ppe2yjedpscp">
              <input type="number" value={block.imageMaxHeight || ''} onChange={e => onChange({
          imageMaxHeight: e.target.value ? Number(e.target.value) : 0
        })} onBlur={onBlur} min={0} placeholder="none" className="prop-input" data-id="element-1139" data-mp="po2y0g976ek0" />
            </PropField>}

          {block.imageSizing === 'natural' && <div className="rounded-lg bg-amber-50 border border-amber-200 p-2.5" data-id="element-1140" data-mp="gpr8khk2hmt5">
              <p className="text-[10px] text-amber-700 leading-relaxed" data-id="element-1141" data-mp="gnel5ovjik8p">
                <strong data-id="element-1142" data-mp="bp3d9jot2jt4">Natural:</strong> The image will render at its original
                dimensions with no constraints. It may overflow the form
                container if it's larger.
              </p>
            </div>}

          <PropField label="Object Fit" data-id="element-1143" data-mp="3su3sxp4g7dq3">
            <select value={block.imageObjectFit || 'contain'} onChange={e => {
          onChange({
            imageObjectFit: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-1144" data-mp="3svf4ib44xprr">
              <option value="contain" data-id="element-1145" data-mp="apabhlc20ws7">Contain (fit inside)</option>
              <option value="cover" data-id="element-1146" data-mp="fo3o7bjiojrb">Cover (fill & crop)</option>
              <option value="fill" data-id="element-1147" data-mp="flr710ll2dfb">Fill (stretch)</option>
              <option value="none" data-id="element-1148" data-mp="kldlfdd5dzpp">None (natural size)</option>
              <option value="scale-down" data-id="element-1149" data-mp="pdn7ujf20hn8">
                Scale Down (smaller of contain/none)
              </option>
            </select>
            {(block.imageObjectFit === 'cover' || block.imageObjectFit === 'contain') && !block.imageHeight && !block.imageMaxHeight && (block.imageSizing || 'maxWidth') !== 'natural' && <p className="text-[10px] text-gray-400 mt-1" data-id="element-1150" data-mp="1dh38uagu7vig">
                  Set a height or max-height to see {block.imageObjectFit} take
                  effect.
                </p>}
          </PropField>

          <PropField label="Position" data-id="element-1151" data-mp="13idtaoceyn5d">
            <select value={block.imagePosition || 'static'} onChange={e => {
          onChange({
            imagePosition: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-1152" data-mp="13jfazbnxq7w2">
              <option value="static" data-id="element-1153" data-mp="1r99ozwjzq2hw">Static (normal flow)</option>
              <option value="relative" data-id="element-1154" data-mp="1mgq0i4jrlmyb">Relative (offset from normal)</option>
              <option value="absolute" data-id="element-1155" data-mp="1igfyt4309ip9">Absolute (relative to form)</option>
              <option value="fixed" data-id="element-1156" data-mp="1ied6gdedrdhd">Fixed (relative to viewport)</option>
              <option value="sticky" data-id="element-1157" data-mp="2eznbl7jt3953">Sticky (sticks on scroll)</option>
            </select>
          </PropField>

          {(block.imagePosition === 'absolute' || block.imagePosition === 'fixed' || block.imagePosition === 'relative') && <>
              <PropField label="Top Offset (px)" data-id="element-1158" data-mp="d7leyiqbp0bn">
                <div className="flex items-center gap-2" data-id="element-1159" data-mp="3upeetwj55ryt">
                  <input type="number" value={block.imageTopOffset || 0} onChange={e => onChange({
              imageTopOffset: Number(e.target.value)
            })} onBlur={onBlur} className="prop-input" data-id="element-1160" data-mp="3uov6n7jamhxz" />
                </div>
              </PropField>
              <PropField label="Left Offset (px)" data-id="element-1161" data-mp="3gpn547znqxqm">
                <div className="flex items-center gap-2" data-id="element-1162" data-mp="3gqepsnrpvb24">
                  <input type="number" value={block.imageLeftOffset || 0} onChange={e => onChange({
              imageLeftOffset: Number(e.target.value)
            })} onBlur={onBlur} className="prop-input" data-id="element-1163" data-mp="19zfary57w31h" />
                </div>
              </PropField>
            </>}

          {block.imagePosition && block.imagePosition !== 'static' && <div className="rounded-lg bg-blue-50 border border-blue-200 p-2.5" data-id="element-1164" data-mp="34em5647zwtbf">
              <p className="text-[10px] text-blue-700 leading-relaxed" data-id="element-1165" data-mp="3i5r5z9n3nrnc">
                <strong className="capitalize" data-id="element-1166" data-mp="3e5gzlvveaz7y">{block.imagePosition}:</strong>{' '}
                {block.imagePosition === 'absolute' && 'Positioned relative to the form container. Use offsets to place it.'}
                {block.imagePosition === 'fixed' && 'Stays fixed on the viewport. Great for watermarks or background logos.'}
                {block.imagePosition === 'sticky' && 'Sticks to the top when scrolling past it.'}
                {block.imagePosition === 'relative' && 'Offset from its normal position. Other elements are not affected.'}
              </p>
            </div>}

          <PropField label="Border Radius" data-id="element-1167" data-mp="1bvf6fr0m78i1">
            <div className="flex items-center gap-2" data-id="element-1168" data-mp="1go8b6ew32gu4">
              <input type="range" min={0} max={50} value={block.imageBorderRadius || 0} onChange={e => onChange({
            imageBorderRadius: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-1169" data-mp="1gquhycb5gsba" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1170" data-mp="y0d39bn5jx8x">
                {block.imageBorderRadius || 0}px
              </span>
            </div>
          </PropField>
        </>}

      {activeTab === 'effects' && <>
          <PropField label="Opacity" data-id="element-1171" data-mp="2eb7cj2xq0cyc">
            <div className="flex items-center gap-2" data-id="element-1172" data-mp="2eeczabivn2qw">
              <input type="range" min={10} max={100} value={block.imageOpacity ?? 100} onChange={e => onChange({
            imageOpacity: Number(e.target.value)
          })} onMouseUp={onBlur} className="flex-1" data-id="element-1173" data-mp="2nvenslny0s6" />
              <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1174" data-mp="3f6k309434ilu">
                {block.imageOpacity ?? 100}%
              </span>
            </div>
          </PropField>

          <PropField label="Shadow" data-id="element-1175" data-mp="365dkxvlsezqh">
            <div className="grid grid-cols-4 gap-1" data-id="element-1176" data-mp="366ezia20i4cy">
              {(['none', 'sm', 'md', 'lg'] as const).map(s => <button key={s} onClick={() => {
            onChange({
              imageShadow: s
            });
            onBlur();
          }} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${(block.imageShadow || 'none') === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1177" data-mp="318dehjl0zuyu">
                  {s === 'none' ? 'Off' : s.toUpperCase()}
                </button>)}
            </div>
          </PropField>

          <PropField label="Animation" data-id="element-1178" data-mp="256auhe36ks62">
            <select value={block.imageAnimation || 'none'} onChange={e => {
          onChange({
            imageAnimation: e.target.value as any
          });
          onBlur();
        }} className="prop-input" data-id="element-1179" data-mp="25596ya4zad7g">
              <option value="none" data-id="element-1180" data-mp="1reco3b0utadl">None</option>
              <option value="fadeIn" data-id="element-1181" data-mp="2oujl3jp2sq3g">Fade In</option>
              <option value="slideUp" data-id="element-1182" data-mp="2ow31daaqf158">Slide Up</option>
              <option value="slideDown" data-id="element-1183" data-mp="2k436hw9iqyfi">Slide Down</option>
              <option value="zoomIn" data-id="element-1184" data-mp="2k2id49yxxlj8">Zoom In</option>
              <option value="blurIn" data-id="element-1185" data-mp="2rfrbst42irhk">Blur In (focus reveal)</option>
            </select>
          </PropField>

          {block.imageAnimation && block.imageAnimation !== 'none' && <div className="rounded-lg bg-purple-50 border border-purple-200 p-2.5" data-id="element-1186" data-mp="31ddt1m8uav4p">
              <p className="text-[10px] text-purple-700 leading-relaxed" data-id="element-1187" data-mp="35eqznyjrb2co">
                <strong className="capitalize" data-id="element-1188" data-mp="35g21hstn6015">{block.imageAnimation}:</strong>{' '}
                {block.imageAnimation === 'fadeIn' && 'Image fades in smoothly when the form loads.'}
                {block.imageAnimation === 'slideUp' && 'Image slides up into view from below.'}
                {block.imageAnimation === 'slideDown' && 'Image slides down into view from above.'}
                {block.imageAnimation === 'zoomIn' && 'Image starts small and zooms to full size.'}
                {block.imageAnimation === 'blurIn' && 'Image starts blurred and gradually comes into focus.'}
              </p>
            </div>}
        </>}
    </>;
}
// ── Section Settings Modal ──
function SectionSettingsModal({
  block,
  onSave,
  onClose
}: {
  block: FormBlock;
  onSave: (updates: Partial<FormBlock>) => void;
  onClose: () => void;
}) {
  const [showTitle, setShowTitle] = useState(block.sectionShowTitle !== false);
  const [animation, setAnimation] = useState(block.sectionAnimation || 'none');
  const [collapsible, setCollapsible] = useState(block.sectionCollapsible || false);
  const [borderStyle, setBorderStyle] = useState(block.sectionBorderStyle || 'solid');
  const [bgColor, setBgColor] = useState(block.sectionBackground || '');
  const [title, setTitle] = useState(block.sectionTitle || '');
  const handleSave = () => {
    onSave({
      sectionTitle: title,
      sectionShowTitle: showTitle,
      sectionAnimation: animation as any,
      sectionCollapsible: collapsible,
      sectionBorderStyle: borderStyle as any,
      sectionBackground: bgColor || undefined
    });
  };
  const animations = [{
    value: 'none',
    label: 'None',
    desc: 'No animation'
  }, {
    value: 'fadeIn',
    label: 'Fade In',
    desc: 'Smoothly fades into view'
  }, {
    value: 'slideLeft',
    label: 'Slide from Left',
    desc: 'Slides in from the left side'
  }, {
    value: 'slideRight',
    label: 'Slide from Right',
    desc: 'Slides in from the right side'
  }, {
    value: 'slideUp',
    label: 'Slide from Bottom',
    desc: 'Slides up from below'
  }, {
    value: 'slideDown',
    label: 'Slide from Top',
    desc: 'Slides down from above'
  }, {
    value: 'zoomIn',
    label: 'Zoom In',
    desc: 'Scales up from small to full size'
  }];
  return <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm" onClick={onClose} data-id="element-1189" data-mp="9iamuo59fr8w">
      <div className="bg-white rounded-xl shadow-2xl w-[480px] max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-1190" data-mp="3kxkwvt9g9j6f">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200" data-id="element-1191" data-mp="3azerv2scjwxc">
          <div className="flex items-center gap-2.5" data-id="element-1192" data-mp="360lnbzv1fg9b">
            <div className="p-2 bg-blue-100 rounded-lg" data-id="element-1193" data-mp="36025sk9autvt">
              <SettingsIcon className="h-4 w-4 text-blue-600" data-id="element-1194" data-mp="31zic5qnxq8ef" />
            </div>
            <div data-id="element-1195" data-mp="2x8a1kravcgzo">
              <h3 className="text-sm font-semibold text-gray-900" data-id="element-1196" data-mp="2x7qgx2tu3ehy">
                Section Settings
              </h3>
              <p className="text-[11px] text-gray-500" data-id="element-1197" data-mp="2sa8hq45mcdn5">
                Configure how this section appears and behaves
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1198" data-mp="2iardlr60m7ta">
            <XIcon className="h-4 w-4" data-id="element-1199" data-mp="2ebiioiz8o3w5" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5" data-id="element-1200" data-mp="ttg2nqk2e4sg">
          {/* Title */}
          <div data-id="element-1201" data-mp="xr5hb856pxnt">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5" data-id="element-1202" data-mp="xutbm9igeo3l">
              Section Title
            </label>
            <input type="text" value={title} onChange={e => setTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent" placeholder="e.g. Personal Information" data-id="element-1203" data-mp="17ro86mv62ef8" />
          </div>

          {/* Show/Hide Title */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-1204" data-mp="osk0pij9wspo">
            <div className="flex items-center gap-2.5" data-id="element-1205" data-mp="3nisk2sp8h2fs">
              {showTitle ? <EyeIcon className="h-4 w-4 text-gray-500" data-id="element-1206" data-mp="3sh3v36r9lizl" /> : <EyeOffIcon className="h-4 w-4 text-gray-400" data-id="element-1207" data-mp="3sf0u5rokq5g3" />}
              <div data-id="element-1208" data-mp="3keuw7b0xuo41">
                <p className="text-xs font-medium text-gray-700" data-id="element-1209" data-mp="3kebdvt7dlxph">
                  Show Section Title
                </p>
                <p className="text-[10px] text-gray-500" data-id="element-1210" data-mp="3u67kjsnj2w5b">
                  Display the title above the section fields
                </p>
              </div>
            </div>
            <button onClick={() => setShowTitle(!showTitle)} className={`relative w-10 h-5.5 rounded-full transition-colors ${showTitle ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1211" data-mp="3p9qomca37dho">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${showTitle ? 'translate-x-5' : ''}`} data-id="element-1212" data-mp="3bfpxa3lgssrc" />
            </button>
          </div>

          {/* Collapsible */}
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-id="element-1213" data-mp="qo79u5ql8co0">
            <div className="flex items-center gap-2.5" data-id="element-1214" data-mp="lpvzzv0am25s">
              <ChevronDownIcon className="h-4 w-4 text-gray-500" data-id="element-1215" data-mp="23ok96imow2t8" />
              <div data-id="element-1216" data-mp="23mzhd0rrexz2">
                <p className="text-xs font-medium text-gray-700" data-id="element-1217" data-mp="28ndo8fhmk1vi">Collapsible</p>
                <p className="text-[10px] text-gray-500" data-id="element-1218" data-mp="28lanp1iuiqcj">
                  Allow users to expand/collapse this section
                </p>
              </div>
            </div>
            <button onClick={() => setCollapsible(!collapsible)} className={`relative w-10 h-5.5 rounded-full transition-colors ${collapsible ? 'bg-blue-600' : 'bg-gray-300'}`} data-id="element-1219" data-mp="1znibi68f7ytd">
              <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${collapsible ? 'translate-x-5' : ''}`} data-id="element-1220" data-mp="2rfzg6opev8at" />
            </button>
          </div>

          {/* Border Style */}
          <div data-id="element-1221" data-mp="36avthgc2ulur">
            <label className="block text-xs font-semibold text-gray-700 mb-2" data-id="element-1222" data-mp="36ads65ak5hfz">
              Border Style
            </label>
            <div className="grid grid-cols-4 gap-2" data-id="element-1223" data-mp="3b3yrq1rsujha">
              {[{
              value: 'none',
              label: 'None',
              preview: 'border-transparent'
            }, {
              value: 'solid',
              label: 'Solid',
              preview: 'border-gray-300'
            }, {
              value: 'dashed',
              label: 'Dashed',
              preview: 'border-gray-300 border-dashed'
            }, {
              value: 'accent',
              label: 'Accent',
              preview: 'border-l-4 border-l-blue-500 border-gray-200'
            }].map(opt => <button key={opt.value} onClick={() => setBorderStyle(opt.value)} className={`flex flex-col items-center gap-1.5 p-2.5 rounded-lg border-2 transition-all ${borderStyle === opt.value ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'}`} data-id="element-1224" data-mp="1fzhb7cl5qb4n">
                  <div className={`w-full h-6 rounded border ${opt.preview}`} data-id="element-1225" data-mp="2g4t5kgtrirbm" />
                  <span className={`text-[10px] font-medium ${borderStyle === opt.value ? 'text-blue-700' : 'text-gray-600'}`} data-id="element-1226" data-mp="2k90ry7cqzv6o">
                    {opt.label}
                  </span>
                </button>)}
            </div>
          </div>

          {/* Background Color */}
          <div data-id="element-1227" data-mp="33wgni991qs1r">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5" data-id="element-1228" data-mp="33w8cyo0ebytq">
              Background Color
            </label>
            <div className="flex items-center gap-2" data-id="element-1229" data-mp="37zdb6edg1eck">
              <input type="color" value={bgColor || '#ffffff'} onChange={e => setBgColor(e.target.value)} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1230" data-mp="3cx6dgwbmlo7k" />
              <input type="text" value={bgColor} onChange={e => setBgColor(e.target.value)} placeholder="Leave empty for transparent" className="flex-1 px-3 py-1.5 text-xs font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" data-id="element-1231" data-mp="1ijdveefbkqny" />
              {bgColor && <button onClick className="p-1 text-gray-400 hover:text-gray-600" data-id="element-1232" data-mp="z640610m8axs">
                  <XIcon className="h-3.5 w-3.5" data-id="element-1233" data-mp="1ru9o0wmyug1u" />
                </button>}
            </div>
          </div>

          <div className="pt-2 border-t border-gray-200" data-id="element-1234" data-mp="1dvibhenr52ft">
            <p className="text-[10px] text-gray-400" data-id="element-1235" data-mp="1dx34v0ybyfc3">
              Drag fields onto this section to group them. Click the ⚙️ gear
              icon for more settings. Use logic rules to show/hide entire
              sections.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-end gap-2" data-id="element-1236" data-mp="6eu5s1dbuxzw">
          <button onClick={onClose} className="px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1237" data-mp="3v4v3u1civ7t7">
            Cancel
          </button>
          <button onClick={handleSave} className="px-3 py-1.5 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors" data-id="element-1238" data-mp="3wqygwkdfyx1">
            Save
          </button>
        </div>
      </div>
    </div>;
}
// ── Theme Properties Panel ──
function ThemeProperties(props: {
  theme: FormTheme;
  onChange: (t: FormTheme) => void;
}) {
  var theme = props.theme;
  var onChange = props.onChange;
  return <div className="p-4 space-y-4" data-id="element-1239" data-mp="3hac8uaywr6kx">
      <div className="pb-3 border-b border-gray-200" data-id="element-1240" data-mp="2tm2ozel98swf">
        <h3 className="text-sm font-semibold text-gray-900" data-id="element-1241" data-mp="2tkhyq15z3w4d">Form Theme</h3>
        <p className="text-[11px] text-gray-500 mt-0.5" data-id="element-1242" data-mp="2olwvrtzc6yc8">
          Global styles for the form
        </p>
      </div>

      <PropField label="Font Family" data-id="element-1243" data-mp="3eabzc9iymbt2">
        <select value={theme.fontFamily} onChange={e => onChange({
        ...theme,
        fontFamily: e.target.value
      })} className="prop-input" data-id="element-1244" data-mp="3e9ani2u4dlaa">
          <option value="Inter, system-ui, sans-serif" data-id="element-1245" data-mp="3s2s17704k3se">Inter</option>
          <option value="'Helvetica Neue', Helvetica, Arial, sans-serif" data-id="element-1246" data-mp="2vc9apvubqgvs">
            Helvetica
          </option>
          <option value="Georgia, 'Times New Roman', serif" data-id="element-1247" data-mp="2zdbaqew2ewyt">Georgia</option>
          <option value="'Courier New', monospace" data-id="element-1248" data-mp="34cxmsl05q761">Courier New</option>
          <option value="system-ui, sans-serif" data-id="element-1249" data-mp="34bmhuhuz6tdc">System UI</option>
        </select>
      </PropField>

      <PropField label="Primary Color" data-id="element-1250" data-mp="39dbxybx21x8a">
        <div className="flex items-center gap-2" data-id="element-1251" data-mp="jmyrbb0atqkb">
          <input type="color" value={theme.primaryColor} onChange={e => onChange({
          ...theme,
          primaryColor: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1252" data-mp="jm700cdqiezu" />
          <input type="text" value={theme.primaryColor} onChange={e => onChange({
          ...theme,
          primaryColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1253" data-mp="1zdrb2jzf5o35" />
        </div>
      </PropField>

      <PropField label="Background" data-id="element-1254" data-mp="1ldgjfeqac5gx">
        <div className="space-y-2" data-id="element-1255" data-mp="1lgm84sv055c4">
          <select value={theme.backgroundType} onChange={e => onChange({
          ...theme,
          backgroundType: e.target.value as any
        })} className="prop-input" data-id="element-1256" data-mp="1gen6bnewugha">
            <option value="solid" data-id="element-1257" data-mp="35u3bystmlk2f">Solid</option>
            <option value="gradient" data-id="element-1258" data-mp="35vegiuuwavul">Gradient</option>
          </select>
          {theme.backgroundType === 'solid' ? <div className="flex items-center gap-2" data-id="element-1259" data-mp="3dvkdb86so752">
              <input type="color" value={theme.backgroundColor} onChange={e => onChange({
            ...theme,
            backgroundColor: e.target.value
          })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1260" data-mp="3iu5h1hl998y9" />
              <input type="text" value={theme.backgroundColor} onChange={e => onChange({
            ...theme,
            backgroundColor: e.target.value
          })} className="prop-input flex-1 font-mono text-xs" data-id="element-1261" data-mp="2zvjgc3tnhjuv" />
            </div> : <div className="space-y-2" data-id="element-1262" data-mp="2g6si65fn7la9">
              <div className="flex items-center gap-2" data-id="element-1263" data-mp="2g5qxracqlmfv">
                <input type="color" value={theme.gradientFrom} onChange={e => onChange({
              ...theme,
              gradientFrom: e.target.value
            })} className="h-7 w-7 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1264" data-mp="1xigkhz9s6lo3" />
                <span className="text-[10px] text-gray-500 w-8" data-id="element-1265" data-mp="2gh2lzf97mesj">From</span>
                <input type="color" value={theme.gradientTo} onChange={e => onChange({
              ...theme,
              gradientTo: e.target.value
            })} className="h-7 w-7 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1266" data-mp="2bij1lja9lb2a" />
                <span className="text-[10px] text-gray-500" data-id="element-1267" data-mp="2u90tjln9w2ml">To</span>
              </div>
              <select value={theme.gradientDirection} onChange={e => onChange({
            ...theme,
            gradientDirection: e.target.value as any
          })} className="prop-input" data-id="element-1268" data-mp="2u7653xcslsdr">
                <option value="to bottom" data-id="element-1269" data-mp="25mpuhfbsicdu">Top → Bottom</option>
                <option value="to right" data-id="element-1270" data-mp="25p2judjvnp0x">Left → Right</option>
                <option value="to bottom right" data-id="element-1271" data-mp="2al135mqcmguh">Diagonal ↘</option>
                <option value="to top right" data-id="element-1272" data-mp="2fkx75lz1q8a1">Diagonal ↗</option>
              </select>
            </div>}
        </div>
      </PropField>

      <PropField label="Border Radius" data-id="element-1273" data-mp="2jo27bhvnlxvi">
        <div className="flex items-center gap-2" data-id="element-1274" data-mp="2jkxszroh5lr4">
          <input type="range" min={0} max={24} value={theme.borderRadius} onChange={e => onChange({
          ...theme,
          borderRadius: Number(e.target.value)
        })} className="flex-1" data-id="element-1275" data-mp="2okka4c7fbm57" />
          <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1276" data-mp="mqedhgj5wygr">
            {theme.borderRadius}px
          </span>
        </div>
      </PropField>

      <PropField label="Label Color" data-id="element-1277" data-mp="cxqjsunhbam9">
        <div className="flex items-center gap-2" data-id="element-1278" data-mp="cyrvn1cbk151">
          <input type="color" value={theme.labelColor} onChange={e => onChange({
          ...theme,
          labelColor: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1279" data-mp="7zxctzecqckx" />
          <input type="text" value={theme.labelColor} onChange={e => onChange({
          ...theme,
          labelColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1280" data-mp="vnezjgirekh1" />
        </div>
      </PropField>

      <PropField label="Label Size" data-id="element-1281" data-mp="1fczfbyjuuf6h">
        <div className="flex items-center gap-2" data-id="element-1282" data-mp="1fby33qr3rmn6">
          <input type="range" min={10} max={20} value={theme.labelFontSize} onChange={e => onChange({
          ...theme,
          labelFontSize: Number(e.target.value)
        })} className="flex-1" data-id="element-1283" data-mp="1ffc7f4jbu5x9" />
          <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1284" data-mp="1z0gnzw52sdw2">
            {theme.labelFontSize}px
          </span>
        </div>
      </PropField>

      <PropField label="Label Weight" data-id="element-1285" data-mp="231shwduzfla2">
        <select value={theme.labelFontWeight} onChange={e => onChange({
        ...theme,
        labelFontWeight: e.target.value as any
      })} className="prop-input" data-id="element-1286" data-mp="1pbf9ybi3c6kq">
          <option value="400" data-id="element-1287" data-mp="2hyji2pchd10q">Regular</option>
          <option value="500" data-id="element-1288" data-mp="2mr2uvk3cfs4h">Medium</option>
          <option value="600" data-id="element-1289" data-mp="2ms491xfnouqf">Semibold</option>
          <option value="700" data-id="element-1290" data-mp="2w54no6daeulw">Bold</option>
        </select>
      </PropField>

      <PropField label="Input Border" data-id="element-1291" data-mp="2w30aexti0j8y">
        <div className="flex items-center gap-2" data-id="element-1292" data-mp="2s4kokx95lgx4">
          <input type="color" value={theme.inputBorderColor} onChange={e => onChange({
          ...theme,
          inputBorderColor: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1293" data-mp="2s1ortc91kya9" />
          <input type="text" value={theme.inputBorderColor} onChange={e => onChange({
          ...theme,
          inputBorderColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1294" data-mp="295otzktdv0ir" />
        </div>
      </PropField>

      <PropField label="Input Background" data-id="element-1295" data-mp="2gk1uf1ptuou">
        <div className="flex items-center gap-2" data-id="element-1296" data-mp="3tljutz68z1j9">
          <input type="color" value={theme.inputBackground} onChange={e => onChange({
          ...theme,
          inputBackground: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1297" data-mp="3tmuvvr8b5v6o" />
          <input type="text" value={theme.inputBackground} onChange={e => onChange({
          ...theme,
          inputBackground: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1298" data-mp="12ixmi2hv3k8k" />
        </div>
      </PropField>

      <PropField label="Input Focus Color" data-id="element-1299" data-mp="1qaun0xfv2pud">
        <div className="flex items-center gap-2" data-id="element-1300" data-mp="1l95bzhsn2c6a">
          <input type="color" value={theme.inputFocusColor} onChange={e => onChange({
          ...theme,
          inputFocusColor: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1301" data-mp="1lagme0glai6d" />
          <input type="text" value={theme.inputFocusColor} onChange={e => onChange({
          ...theme,
          inputFocusColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1302" data-mp="2ivrnrje6mqhq" />
        </div>
      </PropField>

      <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1303" data-mp="1v4cjvm6rc14d">
        <input type="checkbox" checked={theme.inputShadow} onChange={e => onChange({
        ...theme,
        inputShadow: e.target.checked
      })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1304" data-mp="1zxfbhorz50hx" />
        Input shadow
      </label>

      <PropField label="Button Style" data-id="element-1305" data-mp="1c95o4if444oo">
        <div className="flex gap-1" data-id="element-1306" data-mp="1h4uognyyhpm1">
          {(['filled', 'outline'] as const).map(s => <button key={s} onClick={() => onChange({
          ...theme,
          buttonStyle: s
        })} className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border transition-colors capitalize ${theme.buttonStyle === s ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1307" data-mp="3ivbqjn64kfhm">
              {s}
            </button>)}
        </div>
      </PropField>

      <PropField label="Button Text Color" data-id="element-1308" data-mp="q8d9f608x64">
        <div className="flex items-center gap-2" data-id="element-1309" data-mp="3sszmggfgvt1d">
          <input type="color" value={theme.buttonTextColor} onChange={e => onChange({
          ...theme,
          buttonTextColor: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1310" data-mp="3sreoegty1ryr" />
          <input type="text" value={theme.buttonTextColor} onChange={e => onChange({
          ...theme,
          buttonTextColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1311" data-mp="rxh04r0gmh4a" />
        </div>
      </PropField>

      <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1312" data-mp="15pwu72bdv4t2">
        <input type="checkbox" checked={theme.buttonShadow} onChange={e => onChange({
        ...theme,
        buttonShadow: e.target.checked
      })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1313" data-mp="1amdj3yqepn77" />
        Button shadow
      </label>

      <PropField label="Heading Color" data-id="element-1314" data-mp="1okvdgfathxy3">
        <div className="flex items-center gap-2" data-id="element-1315" data-mp="3mdrb8ktd6vcq">
          <input type="color" value={theme.headingColor} onChange={e => onChange({
          ...theme,
          headingColor: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1316" data-mp="3mf29y67ydcwz" />
          <input type="text" value={theme.headingColor} onChange={e => onChange({
          ...theme,
          headingColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1317" data-mp="352u3uzx3116w" />
        </div>
      </PropField>

      <PropField label="Paragraph Color" data-id="element-1318" data-mp="27gn17vkc9ix6">
        <div className="flex items-center gap-2" data-id="element-1319" data-mp="2m3g8qenm9ket">
          <input type="color" value={theme.paragraphColor} onChange={e => onChange({
          ...theme,
          paragraphColor: e.target.value
        })} className="h-8 w-8 rounded border border-gray-300 cursor-pointer flex-shrink-0" data-id="element-1320" data-mp="2m5av7xejdkl0" />
          <input type="text" value={theme.paragraphColor} onChange={e => onChange({
          ...theme,
          paragraphColor: e.target.value
        })} className="prop-input flex-1 font-mono text-xs" data-id="element-1321" data-mp="46joa6bya3mp" />
        </div>
      </PropField>

      <PropField label="Form Padding" data-id="element-1322" data-mp="3vd4ancr28nde">
        <div className="flex items-center gap-2" data-id="element-1323" data-mp="mwrmqb4pg5wk">
          <input type="range" min={8} max={64} value={theme.formPadding} onChange={e => onChange({
          ...theme,
          formPadding: Number(e.target.value)
        })} className="flex-1" data-id="element-1324" data-mp="my2pc7mez7m3" />
          <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1325" data-mp="ixah9hpa38re">
            {theme.formPadding}px
          </span>
        </div>
      </PropField>

      <PropField label="Field Spacing" data-id="element-1326" data-mp="33loqrqgw1eqv">
        <div className="flex items-center gap-2" data-id="element-1327" data-mp="33mq4y3t7ahct">
          <input type="range" min={8} max={48} value={theme.fieldSpacing} onChange={e => onChange({
          ...theme,
          fieldSpacing: Number(e.target.value)
        })} className="flex-1" data-id="element-1328" data-mp="195pzm68s0kp2" />
          <span className="text-xs text-gray-500 w-10 text-right" data-id="element-1329" data-mp="vd0avu1v51o9">
            {theme.fieldSpacing}px
          </span>
        </div>
      </PropField>

      <PropField label="Container Shadow" data-id="element-1330" data-mp="lfm12dag3b4x">
        <div className="grid grid-cols-4 gap-1" data-id="element-1331" data-mp="leuett2qmprb">
          {(['none', 'sm', 'md', 'lg'] as const).map(s => <button key={s} onClick={() => onChange({
          ...theme,
          containerShadow: s
        })} className={`py-1.5 text-[10px] font-medium rounded-md border transition-colors capitalize ${theme.containerShadow === s ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`} data-id="element-1332" data-mp="gh154q287dma">
              {s === 'none' ? 'Off' : s.toUpperCase()}
            </button>)}
        </div>
      </PropField>

      <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1333" data-mp="2c1z072i4yie">
        <input type="checkbox" checked={theme.containerBorder} onChange={e => onChange({
        ...theme,
        containerBorder: e.target.checked
      })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1334" data-mp="6cceqbg3j3ag" />
        Container border
      </label>

      <PropField label="Layout Mode" data-id="element-1335" data-mp="3er2vdsv4n1zg">
        <div className="flex gap-1" data-id="element-1336" data-mp="3jr8u1sztdor1">
          {(['standard', 'card'] as const).map(m => <button key={m} onClick={() => onChange({
          ...theme,
          layoutMode: m
        })} className={`flex-1 px-2 py-1.5 text-xs font-medium rounded border transition-colors capitalize ${theme.layoutMode === m ? 'bg-blue-50 border-blue-300 text-blue-700' : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'}`} data-id="element-1337" data-mp="u2f42jsmlv5d">
              {m}
            </button>)}
        </div>
      </PropField>

      {theme.layoutMode === 'card' && <div className="space-y-3 pt-2 border-t border-gray-100" data-id="element-1338" data-mp="eo6rxtkycbsh">
          <p className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-1339" data-mp="tdw2gfexps5f">
            Card Mode Settings
          </p>
          <PropField label="Card Width" data-id="element-1340" data-mp="oigrbv8baacp">
            <div className="flex items-center gap-2" data-id="element-1341" data-mp="12g5fbbxodxlz">
              <input type="range" min={320} max={960} step={20} value={theme.cardWidth} onChange={e => onChange({
            ...theme,
            cardWidth: Number(e.target.value)
          })} className="flex-1" data-id="element-1342" data-mp="12dj8jeilzm4t" />
              <span className="text-xs text-gray-500 w-12 text-right" data-id="element-1343" data-mp="2q42rn0u6xnc8">
                {theme.cardWidth}px
              </span>
            </div>
          </PropField>
          <PropField label="Prev Label" data-id="element-1344" data-mp="2gbdpiwkj5ltk">
            <input type="text" value={theme.cardPrevLabel} onChange={e => onChange({
          ...theme,
          cardPrevLabel: e.target.value
        })} className="prop-input" data-id="element-1345" data-mp="2lb01l2omgw0s" />
          </PropField>
          <PropField label="Next Label" data-id="element-1346" data-mp="1xdsybiuamb5s">
            <input type="text" value={theme.cardNextLabel} onChange={e => onChange({
          ...theme,
          cardNextLabel: e.target.value
        })} className="prop-input" data-id="element-1347" data-mp="1xf45ztqv0326" />
          </PropField>
          <PropField label="Nav Style" data-id="element-1348" data-mp="12cnjs3cg3yff">
            <select value={theme.cardNavStyle} onChange={e => onChange({
          ...theme,
          cardNavStyle: e.target.value as any
        })} className="prop-input" data-id="element-1349" data-mp="12dq8q5q7jqsp">
              <option value="text" data-id="element-1350" data-mp="1h2cyz2hdi2yp">Text</option>
              <option value="icons" data-id="element-1351" data-mp="1h34lzowwmsdd">Icons</option>
              <option value="pills" data-id="element-1352" data-mp="1l1va56je6zoc">Pills</option>
            </select>
          </PropField>
          <PropField label="Transition" data-id="element-1353" data-mp="1q16s2ivhwb7i">
            <select value={theme.cardTransition} onChange={e => onChange({
          ...theme,
          cardTransition: e.target.value as any
        })} className="prop-input" data-id="element-1354" data-mp="1q0581owi4edn">
              <option value="fade" data-id="element-1355" data-mp="owacpdoa3gi">Fade</option>
              <option value="slide" data-id="element-1356" data-mp="5lmo36208myr">Slide</option>
              <option value="none" data-id="element-1357" data-mp="5memspqwf0t9">None</option>
            </select>
          </PropField>
          <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1358" data-mp="2ixz7xsc5ohia">
            <input type="checkbox" checked={theme.cardShowProgress} onChange={e => onChange({
          ...theme,
          cardShowProgress: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1359" data-mp="2e4mwkjwhm77h" />
            Show progress bar
          </label>
          <label className="flex items-center gap-2 text-sm text-gray-700" data-id="element-1360" data-mp="2xuzy3rep84en">
            <input type="checkbox" checked={theme.cardShowCounter} onChange={e => onChange({
          ...theme,
          cardShowCounter: e.target.checked
        })} className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" data-id="element-1361" data-mp="2stss9xw49fam" />
            Show step counter
          </label>
        </div>}
    </div>;
}